(function () {
  const CONFIG_SELECTOR = '[data-product-integrations-config]';
  const OPTION_HELP_SELECTOR = '[data-product-option-help]';
  const PRODUCT_OPTIONS_SELECTOR = '.bss-po-product-page';
  const OPTION_HELP_DRAWER_ID = 'option-help-drawer';
  const OPTION_DECORATION_OBSERVE_MS = 5000;
  const CONDITIONAL_OPTION_LABEL_SEPARATOR = /[-–—]/;
  const REVIEW_SELECTORS = [
    '.jdgm-preview-badge',
    '.shopify-product-reviews-badge',
    '.spr-badge',
    '.product-reviews',
  ];

  let deferredProductStylesUrl = '';
  let deferredProductStylesPromise = null;
  let measurementToolAssetsPromise = null;
  let productRumAssetPromise = null;

  function loadDeferredProductStyles() {
    if (!deferredProductStylesUrl) return Promise.resolve();
    if (deferredProductStylesPromise) return deferredProductStylesPromise;

    const resolvedStylesUrl = new URL(deferredProductStylesUrl, document.baseURI).href;
    const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .find((link) => link.href === resolvedStylesUrl);
    if (existing) {
      deferredProductStylesPromise = Promise.resolve(existing);
      return deferredProductStylesPromise;
    }

    deferredProductStylesPromise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = deferredProductStylesUrl;
      link.addEventListener('load', () => resolve(link), { once: true });
      link.addEventListener('error', reject, { once: true });
      document.head.appendChild(link);
    }).catch((error) => {
      deferredProductStylesPromise = null;
      console.warn('Unable to load deferred product styles', error);
    });

    return deferredProductStylesPromise;
  }

  function loadMeasurementToolAssets(config) {
    const root = getRoot(config);
    const template = root?.querySelector('template[data-curtain-measurement-template]');
    if (template) template.replaceWith(template.content.cloneNode(true));
    if (!root?.querySelector('[data-curtain-measurement-root]')) return Promise.resolve(false);
    if (measurementToolAssetsPromise) return measurementToolAssetsPromise;

    const loadStyle = () => {
      if (!config.measurementCssUrl) return Promise.resolve();
      const resolvedUrl = new URL(config.measurementCssUrl, document.baseURI).href;
      const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .find((link) => link.href === resolvedUrl);
      if (existing) return Promise.resolve(existing);

      return new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = config.measurementCssUrl;
        link.addEventListener('load', () => resolve(link), { once: true });
        link.addEventListener('error', reject, { once: true });
        document.head.appendChild(link);
      });
    };

    const loadScript = () => {
      if (!config.measurementJsUrl || window.CurtainMeasurement) return Promise.resolve();
      const resolvedUrl = new URL(config.measurementJsUrl, document.baseURI).href;
      const existing = Array.from(document.querySelectorAll('script[src]'))
        .find((script) => script.src === resolvedUrl);
      if (existing) return Promise.resolve(existing);

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = config.measurementJsUrl;
        script.addEventListener('load', () => resolve(script), { once: true });
        script.addEventListener('error', reject, { once: true });
        document.head.appendChild(script);
      });
    };

    measurementToolAssetsPromise = Promise.all([loadStyle(), loadScript()])
      .then(() => {
        window.CurtainMeasurement?.init();
        document.dispatchEvent(new CustomEvent('curtain-measurement:mount'));
        return true;
      })
      .catch((error) => {
        measurementToolAssetsPromise = null;
        console.warn('Unable to load measurement assets', error);
        return false;
      });
    return measurementToolAssetsPromise;
  }

  function classifyProductInteraction(target) {
    if (!(target instanceof Element)) return 'other';
    if (target.closest('media-gallery, zoom-action, .mg__thumb')) return 'gallery';
    if (target.closest('.product-view-native-variant-swatch, variant-radios-detail .product-form__input_color input[type="radio"], .po-option--swatch-container')) return 'color_swatch';
    if (target.closest('.po-dropdown, .po-dropdown-option, .bss-po-product-page .po-option--dropdown-menu-container, .bss-po-product-page .po-select-dropdown-thumbnail, .bss-po-product-page select')) return 'bss_dropdown';
    if (target.closest('quantity-input, .quantity-input')) return 'quantity';
    if (target.closest('.product_submit_button, [name="add"]')) return 'add_to_cart';
    if (target.closest('.mc-accordion, details, summary')) return 'accordion';
    if (target.closest('.jdgm-widget, .jdgm-preview-badge, .product-view-review-slot')) return 'review';
    return 'other';
  }

  function initProductViewRum(config) {
    const debugMode = new URLSearchParams(window.location.search).get('product_rum_debug') === '1';
    const normalizeHost = (host) => String(host || '').toLowerCase().replace(/^www\./, '');
    const isProductionHost = normalizeHost(window.location.hostname) === normalizeHost(config.productRumProductionHost);
    const isLiveTheme = window.Shopify?.theme?.role === 'main';
    const requestedSampleRate = Number(config.productRumSampleRate);
    const sampleRate = Number.isFinite(requestedSampleRate)
      ? Math.min(1, Math.max(0, requestedSampleRate))
      : 0.1;

    if (!config.productRumJsUrl || (!debugMode && (window.Shopify?.designMode || !isLiveTheme || !isProductionHost))) return;
    if (!debugMode && Math.random() >= sampleRate) return;

    const initialize = () => window.ProductRum?.init({
      config: {
        debug: debugMode,
        productionHost: config.productRumProductionHost,
        measurementId: config.productRumMeasurementId,
        releaseVersion: config.productRumReleaseVersion,
        sampleRate,
        sampled: true,
        template: config.productTemplateSuffix || 'default',
      },
      classifyInteraction: classifyProductInteraction,
    });

    if (window.ProductRum) {
      initialize();
      return;
    }
    if (productRumAssetPromise) return;

    productRumAssetPromise = new Promise((resolve, reject) => {
      const resolvedUrl = new URL(config.productRumJsUrl, document.baseURI).href;
      const assetUrl = new URL(resolvedUrl);
      if (debugMode && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)) {
        assetUrl.protocol = window.location.protocol;
        assetUrl.host = window.location.host;
        assetUrl.searchParams.set('rum_debug', Date.now().toString());
      }
      const existing = Array.from(document.querySelectorAll('script[src]'))
        .find((script) => script.src === assetUrl.href);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = assetUrl.href;
      script.async = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    })
      .then(initialize)
      .catch((error) => {
        productRumAssetPromise = null;
        console.warn('Unable to load product RUM', error);
      });
  }

  class ProductRecommendations extends HTMLElement {
    init() {
      if (
        this.recommendationsObserver ||
        this.recommendationsLoading ||
        this.dataset.recommendationsLoaded === 'true' ||
        !this.dataset.url
      ) return;

      if (!('IntersectionObserver' in window)) {
        this.loadRecommendations();
        return;
      }

      this.recommendationsObserver = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.recommendationsObserver.disconnect();
        this.recommendationsObserver = null;
        this.loadRecommendations();
      }, {
        rootMargin: '0px 0px 800px 0px',
      });
      this.recommendationsObserver.observe(this);
    }

    connectedCallback() {
      this.previewEvents?.abort();
      this.previewEvents = new AbortController();
      this.addEventListener('click', (event) => this.openRecommendationPreview(event), { signal: this.previewEvents.signal });
      this.addEventListener('keydown', (event) => {
        if (event.key === ' ' && event.target.matches('[data-recommendation-preview-trigger]')) {
          event.preventDefault();
          event.target.click();
        }
      }, { signal: this.previewEvents.signal });
      if (this.dataset.recommendationsLoaded === 'true') {
        this.observeRecommendationSwatches();
        this.preparePreviewTriggers();
        return;
      }
      this.init();
    }

    disconnectedCallback() {
      this.recommendationsObserver?.disconnect();
      this.recommendationsObserver = null;
      this.recommendationsRequest?.abort();
      this.recommendationsRequest = null;
      this.recommendationsLoading = false;
      this.removeAttribute('aria-busy');
      this.swatchResizeObserver?.disconnect();
      this.swatchResizeObserver = null;
      this.previewEvents?.abort();
      this.releasePreviewDrawer();
      this.previewCache?.forEach((entry) => entry.controller.abort());
      this.previewCache?.clear();
    }

    observeRecommendationSwatches() {
      this.swatchResizeObserver?.disconnect();
      if (!this.classList.contains('product-recommendations--list')) return;
      const rows = new Map();
      const observer = this.swatchResizeObserver = new ResizeObserver((entries) => {
        entries.forEach(({ target }) => this.fitRecommendationSwatches(rows.get(target)));
      });
      this.querySelectorAll('.product-item__swatches[data-color-count]').forEach((container) => {
        const list = container.querySelector('.product-item__swatch-list');
        const chips = Array.from(list?.querySelectorAll('.product-item__swatch') || []);
        if (!chips.length) return;
        let more = container.querySelector('.product-item__swatch-more');
        if (!more) {
          // Even a product with six or fewer colors may need a count in a narrow column.
          const productLink = container.closest('[data-product-card]')?.querySelector('.product-item__name[href]');
          if (!productLink) return;
          more = document.createElement('a');
          more.className = 'product-item__swatch-more';
          more.setAttribute('href', productLink.getAttribute('href'));
          container.append(more);
        }
        const row = { container, list, chips, more, total: Number(container.dataset.colorCount) };
        rows.set(container, row);
        this.fitRecommendationSwatches(row);
        observer.observe(container);
      });
      // The count's font can finish loading without changing the row's outer dimensions.
      document.fonts?.ready.then(() => {
        if (this.isConnected && this.swatchResizeObserver === observer) {
          rows.forEach((row) => this.fitRecommendationSwatches(row));
        }
      });
    }

    fitRecommendationSwatches({ container, list, chips, more, total }) {
      if (!container.clientWidth) return;
      list.hidden = false;
      chips.forEach((chip) => { chip.hidden = false; });
      const chipWidth = chips[0].getBoundingClientRect().width;
      const chipGap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const rowGap = parseFloat(getComputedStyle(container).columnGap) || 0;
      let visible = chips.length;
      more.hidden = false;
      // Reserve the whole count link before choosing how many complete chips fit.
      while (visible >= 0) {
        const remaining = total - visible;
        more.textContent = `+${remaining} Colors`;
        const moreStyle = getComputedStyle(more);
        const moreWidth = remaining > 0 ? more.getBoundingClientRect().width
          + (parseFloat(moreStyle.marginInlineStart) || 0) + (visible ? rowGap : 0) : 0;
        const chipsWidth = visible * chipWidth + Math.max(0, visible - 1) * chipGap;
        if (chipsWidth + moreWidth <= container.clientWidth || visible === 0) break;
        visible -= 1;
      }
      chips.forEach((chip, index) => { chip.hidden = index >= visible; });
      list.hidden = visible === 0;
      more.hidden = visible === total;
    }

    preparePreviewTriggers() {
      if (!this.dataset.previewTemplate || !window.themeDrawer) return;
      this.querySelectorAll('.product-recommendations__row a[href]').forEach((link) => {
        // Keep href for unavailable JS and modified clicks; ordinary activation previews.
        link.dataset.recommendationPreviewTrigger = '';
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
      });
    }

    getPreviewDrawer() {
      if (this.previewDrawer) return this.previewDrawer;
      const template = document.getElementById(this.dataset.previewTemplate);
      const manager = window.themeDrawer;
      if (!template?.content || !manager) return null;
      const drawer = template.content.querySelector('.theme-drawer-wrapper')?.cloneNode(true);
      if (!drawer) return null;
      // Each connected owner gets its own identity, including rapid editor replacements.
      ProductRecommendations.previewSequence = (ProductRecommendations.previewSequence || 0) + 1;
      const id = `${drawer.id}-${ProductRecommendations.previewSequence}`;
      drawer.id = id;
      drawer.dataset.drawerId = id;
      drawer.classList.add('ds-section', 'product-recommendations-preview');
      const panel = drawer.querySelector('.theme-drawer-panel');
      const overlay = drawer.querySelector('.theme-drawer-overlay');
      const heading = drawer.querySelector('[data-preview-heading]');
      panel.id = `${id}-panel`;
      overlay.id = `${id}-overlay`;
      heading.id = `${id}-title`;
      panel.setAttribute('aria-labelledby', heading.id);
      panel.removeAttribute('aria-label');
      // The list is a query container; mounting outside it preserves viewport-fixed positioning.
      document.body.append(drawer);
      // Desktop retains the native page scrollbar; the shared shell contains scroll gestures.
      manager.register(id, { position: 'right', lockPageScroll: true, preservePageScrollbar: '(min-width: 1024px)' });
      drawer.addEventListener('theme-drawer:closed', () => {
        this.cancelPreviewImageLoad();
        this.destroyPreviewVideos();
        drawer.removeAttribute('data-preview-open');
        this.previewSelection = null;
        drawer.querySelector('[data-preview-gallery]')?.destroy?.();
      }, { signal: this.previewEvents.signal });
      drawer.addEventListener('click', (event) => {
        if (event.target.closest('[data-preview-close]') || event.target === overlay) {
          manager.close(id);
        } else {
          if (event.target.closest('[data-preview-retry]') && this.previewSelection) {
            drawer.querySelector('[data-preview-close]').focus({ preventScroll: true });
            this.showPreview(this.previewSelection);
            return;
          }
          const color = event.target.closest('button[data-preview-url]');
          if (color) this.selectPreviewColor(color);
        }
      }, { signal: this.previewEvents.signal });
      this.previewDrawer = drawer;
      this.previewWideLayout = window.matchMedia('(min-width: 1024px)');
      // Stable root gutters can shrink CSS viewport units; keep the mobile panel edge to edge.
      const syncPreviewWidth = () => drawer.style.setProperty('--preview-viewport-width', `${window.innerWidth}px`);
      syncPreviewWidth();
      window.addEventListener('resize', syncPreviewWidth, { signal: this.previewEvents.signal });
      // Gallery keys navigate images; focused video keys keep their native playback actions.
      drawer.addEventListener('keydown', (event) => {
        const gallery = event.target.closest('[data-preview-gallery]');
        if (!gallery?.swiper || event.target.closest('video') || this.previewWideLayout.matches || event.altKey || event.ctrlKey || event.metaKey) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'ArrowLeft') gallery.swiper.slidePrev();
        else if (event.key === 'ArrowRight') gallery.swiper.slideNext();
        else gallery.swiper.slideTo(event.key === 'Home' ? 0 : gallery.swiper.slides.length - 1);
      }, { signal: this.previewEvents.signal });
      this.previewWideLayout.addEventListener('change', () => this.configurePreviewGallery(), { signal: this.previewEvents.signal });
      return drawer;
    }

    releasePreviewDrawer() {
      this.cancelPreviewImageLoad();
      this.destroyPreviewVideos();
      const drawer = this.previewDrawer;
      if (!drawer) return;
      this.previewDrawer = null;
      this.previewSelection = null;
      const manager = window.themeDrawer;
      const registration = manager?.drawers[drawer.id];
      const dispose = () => {
        drawer.remove();
        if (manager?.drawers[drawer.id] === registration) delete manager.drawers[drawer.id];
      };
      if (registration?.isOpen || registration?.closeTimer) {
        drawer.addEventListener('theme-drawer:closed', dispose, { once: true });
        if (registration.isOpen) manager.close(drawer.id);
      } else {
        dispose();
      }
    }

    previewUrl(href, productUrl) {
      const url = new URL(href, window.location.origin);
      new URL(productUrl, window.location.origin).searchParams.forEach((value, key) => {
        if (key.startsWith('pr_')) url.searchParams.set(key, value);
      });
      return `${url.pathname}${url.search}${url.hash}`;
    }

    setPreviewColorState(button, content) {
      const previous = content.querySelector('button[data-preview-url][aria-pressed="true"]');
      if (previous !== button) {
        previous?.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-pressed', 'true');
      }
      const label = content.querySelector('[data-preview-color]');
      label.hidden = false;
      const colorLabel = document.createElement('strong');
      colorLabel.textContent = 'Color:';
      label.replaceChildren(colorLabel, ` ${button.getAttribute('aria-label')}`);
      this.previewDrawer.querySelector('[data-preview-choose]').href = button.dataset.previewUrl;
    }

    cancelPreviewImageLoad() {
      this.previewImageLoad?.controller.abort();
      this.previewImageLoad?.gallery?.removeAttribute('aria-busy');
      this.previewImageLoad = null;
    }

    waitForPreviewImage(image, signal) {
      return new Promise((resolve) => {
        const finish = (loaded) => {
          clearTimeout(timeout);
          image.removeEventListener('load', decode);
          image.removeEventListener('error', fail);
          signal.removeEventListener('abort', fail);
          resolve(loaded && !signal.aborted);
        };
        const decode = () => {
          // Decode before mounting, including a cached image, to avoid an empty paint.
          if (image.decode) image.decode().then(() => finish(true), () => finish(image.naturalWidth > 0));
          else finish(image.naturalWidth > 0);
        };
        const fail = () => finish(false);
        const timeout = setTimeout(fail, 12000);
        image.addEventListener('load', decode, { once: true });
        image.addEventListener('error', fail, { once: true });
        signal.addEventListener('abort', fail, { once: true });
        if (signal.aborted) fail();
        else if (image.complete) image.naturalWidth > 0 ? decode() : fail();
      });
    }

    async selectPreviewColor(button, content = this.previewDrawer) {
      const drawer = this.previewDrawer;
      if (content === drawer && button.getAttribute('aria-pressed') === 'true' && !this.previewImageLoad) return;
      this.cancelPreviewImageLoad();
      const gallery = content.querySelector('[data-preview-gallery]');
      const previousColor = gallery?._previewColor;
      this.setPreviewColorState(button, content);
      const request = { controller: new AbortController(), gallery };
      this.previewImageLoad = request;
      const live = content === drawer;
      const currentImage = content.querySelector('.product-recommendations-preview__media img');
      const isCurrent = () => !request.controller.signal.aborted && this.previewDrawer === drawer
        && (!live || ((gallery || currentImage)?.isConnected && window.themeDrawer.drawers[drawer.id]?.isOpen));
      if (live) {
        gallery?.setAttribute('aria-busy', 'true');
        this.previewVideos?.forEach((controller) => controller.setActive(false));
      }
      try {
        let images;
        try { images = JSON.parse(button.dataset.swatchPreviewGallery || '[]'); } catch (_) { images = []; }
        if (!Array.isArray(images)) images = [];
        images = images.slice(0, 6).filter((item) => item?.src);
        if (!images.length && button.dataset.swatchPreviewImage) images = [{
          src: button.dataset.swatchPreviewImage,
          alt: button.dataset.swatchPreviewAlt,
          width: currentImage?.width,
          height: currentImage?.height,
        }];
        const slides = images.map((item, index) => {
          const slide = document.createElement('div');
          slide.className = 'product-recommendations-preview__media swiper-slide';
          const image = document.createElement('img');
          image.alt = item.alt || button.getAttribute('aria-label');
          if (item.width > 0 && item.height > 0) {
            image.width = item.width;
            image.height = item.height;
          }
          image.loading = index === 0 ? 'eager' : 'lazy';
          image.decoding = 'async';
          image.src = item.src;
          slide.append(image);
          return slide;
        });
        if (!slides.length) {
          if (live) this.configurePreviewGallery();
          return;
        }
        const ready = await this.waitForPreviewImage(slides[0].querySelector('img'), request.controller.signal);
        if (!isCurrent()) return;
        if (!ready) {
          // A failed color image keeps the last usable gallery and matching selection.
          if (previousColor) this.setPreviewColorState(previousColor, content);
          if (live) this.configurePreviewGallery();
          return;
        }
        if (gallery) {
          const videos = Array.from(gallery.querySelectorAll('[data-preview-video-slide]'));
          // Product-level videos stay available beside each color's scene images.
          slides.splice(1, 0, ...videos);
          if (live) slides[0].classList.add('product-recommendations-preview__media--entering');
          gallery.querySelector('.swiper-wrapper').replaceChildren(...slides);
          gallery._previewColor = button;
          gallery.scrollTop = 0;
          gallery.scrollLeft = 0;
          if (live) this.configurePreviewGallery();
        } else if (currentImage) {
          currentImage.replaceWith(slides[0].querySelector('img'));
        }
      } finally {
        if (this.previewImageLoad === request) {
          gallery?.removeAttribute('aria-busy');
          this.previewImageLoad = null;
        }
      }
    }

    configurePreviewGallery() {
      const drawer = this.previewDrawer;
      const gallery = drawer?.querySelector('[data-preview-gallery]');
      if (!gallery || !window.themeDrawer.drawers[drawer.id]?.isOpen) return;
      gallery.dataset.galleryCount = gallery.querySelector('.swiper-wrapper').children.length;
      if (this.previewWideLayout.matches || gallery.dataset.galleryCount === '1') {
        if (gallery.swiper || gallery.isInitialized) gallery.destroy?.();
        this.configurePreviewVideos(gallery);
        return;
      }
      // The shared carousel script arrives with the requested product, never with the list.
      customElements.whenDefined('theme-carousel').then(() => {
        if (this.previewDrawer !== drawer || !gallery.isConnected || this.previewWideLayout.matches
          || !window.themeDrawer.drawers[drawer.id]?.isOpen) return;
        // Video controls retain pointer starts, so suppress only native image dragging.
        gallery.querySelectorAll('img').forEach((image) => { image.draggable = false; });
        // activate() initializes once; color updates reuse the same Swiper and its listeners.
        gallery.activate({ reset: true });
        this.configurePreviewVideos(gallery);
      });
    }

    destroyPreviewVideos() {
      this.previewVideoGeneration = (this.previewVideoGeneration || 0) + 1;
      this.previewVideoCleanup?.();
      this.previewVideoCleanup = null;
      this.previewVideos?.forEach((controller) => controller.destroy());
      this.previewVideos = null;
    }

    bindPreviewVideoActivation(gallery) {
      this.previewVideoCleanup?.();
      const swiper = gallery.swiper;
      const sync = () => this.previewVideos?.forEach((controller, video) => controller.setActive(
        this.previewWideLayout.matches || !gallery.swiper
          || video.closest('.swiper-slide') === gallery.swiper.slides[gallery.swiper.activeIndex]
      ));
      swiper?.on('slideChange', sync);
      this.previewVideoCleanup = () => { if (!swiper?.destroyed) swiper?.off('slideChange', sync); };
      sync();
    }

    async configurePreviewVideos(gallery) {
      const videos = Array.from(gallery.querySelectorAll('video[data-preview-video]'));
      const isActive = (video) => this.previewWideLayout.matches || !gallery.swiper
        || video.closest('.swiper-slide') === gallery.swiper.slides[gallery.swiper.activeIndex];
      if (this.previewVideos?.size === videos.length && videos.every((video) => this.previewVideos.has(video))) {
        this.bindPreviewVideoActivation(gallery);
        return;
      }
      this.destroyPreviewVideos();
      if (!videos.length) return;
      const generation = this.previewVideoGeneration;
      try {
        const { ThemeVideoController, videoSources } = await import(/* @vite-ignore */ videos[0].dataset.videoController);
        if (generation !== this.previewVideoGeneration || !gallery.isConnected
          || !window.themeDrawer.drawers[this.previewDrawer?.id]?.isOpen) return;
        this.previewVideos = new Map(videos.map((video) => [video, new ThemeVideoController(video, {
          container: video.parentElement,
          sources: () => ({ key: video.dataset.video, sources: videoSources(video.dataset.video) }),
          active: isActive(video),
          autoplay: false,
          // Prepare visible native controls; offscreen videos and the PDP list load no video.
          loadForControls: true,
          fallbackPoster: video.getAttribute('poster'),
        })]));
        this.bindPreviewVideoActivation(gallery);
      } catch (error) {
        console.warn('Recommendation video could not initialize', error);
      }
    }

    openRecommendationPreview(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const trigger = event.target.closest('.product-recommendations__row a[href]');
      if (!trigger || !this.contains(trigger)) return;
      const row = trigger.closest('.product-recommendations__row');
      const productLink = row.querySelector('.product-item__name[href]');
      if (!productLink || !row.dataset.previewProductId) return;
      const productUrl = productLink.getAttribute('href');
      if (new URL(productUrl, window.location.origin).origin !== window.location.origin) return;
      // Focus before mounting/writing drawer DOM to avoid flushing the new layout in this click.
      if (document.activeElement !== trigger) trigger.focus({ preventScroll: true });
      const drawer = this.getPreviewDrawer();
      if (!drawer) return;
      event.preventDefault();
      this.previewSelection = {
        productId: row.dataset.previewProductId,
        productUrl,
        href: trigger.href,
        isSwatch: trigger.matches('.product-item__swatch'),
        title: productLink.textContent.trim(),
      };
      // The drawer is mounted under body; keep its stable source/list/product
      // context without depending on the runtime drawer sequence or URL query.
      drawer.dataset.contentSection = 'pairs_well_with';
      drawer.dataset.contentEvent = 'recommendation_drawer_click';
      drawer.dataset.contentProduct = row.dataset.previewProductId;
      drawer.dataset.contentList = this.dataset.blockId || '';
      trigger.setAttribute('aria-controls', drawer.id);
      drawer.setAttribute('data-preview-open', 'true');
      // Prepare the loading state while hidden; the shared drawer owns entry focus.
      this.showPreview(this.previewSelection);
      window.themeDrawer.open(drawer.id);
    }

    // Cache successful content and share in-flight requests for this owner/page only.
    // URLs omit variants/attribution so all colors reuse one localized product response.
    loadPreview(selection) {
      const url = new URL(selection.productUrl, window.location.origin);
      url.search = '';
      url.hash = '';
      url.searchParams.set('section_id', 'product-recommendation-preview');
      const key = url.href;
      this.previewCache ||= new Map();
      const cached = this.previewCache.get(key);
      if (cached) return cached.promise;
      const entry = { controller: new AbortController() };
      this.previewCache.set(key, entry);
      entry.promise = (async () => {
        const timeout = setTimeout(() => entry.controller.abort(), 12000);
        try {
          const response = await fetch(url.href, { signal: entry.controller.signal });
          if (!response.ok) throw new Error('Preview request failed');
          const markup = await response.text();
          if (entry.controller.signal.aborted || !this.isConnected) throw new Error('Preview request cancelled');
          // Parse inertly so images are requested only for the mounted product/selected color.
          const html = document.createElement('template');
          html.innerHTML = markup;
          const product = html.content.querySelector('[data-recommendation-preview-product]');
          if (!product || product.dataset.recommendationPreviewProduct !== selection.productId) throw new Error('Preview content unavailable');
          const template = document.createElement('template');
          template.content.append(product);
          this.preparePreviewContent(template.content);
          this.adoptSectionStyles(html.content);
          return template;
        } catch (error) {
          if (this.previewCache.get(key) === entry) this.previewCache.delete(key);
          throw error;
        } finally {
          clearTimeout(timeout);
        }
      })();
      return entry.promise;
    }

    async showPreview(selection) {
      this.cancelPreviewImageLoad();
      this.destroyPreviewVideos();
      const drawer = this.previewDrawer;
      const body = drawer.querySelector('[data-preview-content]');
      const choose = drawer.querySelector('[data-preview-choose]');
      choose.href = this.previewUrl(selection.href, selection.productUrl);
      body.setAttribute('aria-busy', 'true');
      const loading = drawer.querySelector('template[data-preview-loading]').content.cloneNode(true);
      loading.querySelector('[data-preview-loading-title]').textContent = selection.title;
      body.replaceChildren(loading);
      body.scrollTop = 0;
      try {
        const template = await this.loadPreview(selection);
        if (!this.isCurrentPreview(selection, drawer)) return;
        // A cache hit must still let the loading shell paint before cloning/mounting rich content.
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        if (!this.isCurrentPreview(selection, drawer)) return;
        await this.renderPreview(template, selection);
      } catch (error) {
        if (!this.isCurrentPreview(selection, drawer)) return;
        const state = document.createElement('div');
        state.className = 'product-recommendations-preview__error';
        const title = document.createElement('h3');
        title.className = 'product-recommendations-preview__title';
        title.textContent = selection.title;
        const message = document.createElement('p');
        message.setAttribute('role', 'alert');
        message.textContent = 'We couldn’t load this preview. Try again or continue to the product page.';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'ds-btn ds-btn-secondary';
        retry.dataset.previewRetry = '';
        retry.textContent = 'Try again';
        state.append(title, message, retry);
        body.replaceChildren(state);
      } finally {
        if (this.isCurrentPreview(selection, drawer)) body.removeAttribute('aria-busy');
      }
    }

    isCurrentPreview(selection, drawer) {
      return this.isConnected && this.previewDrawer === drawer && this.previewSelection === selection
        && window.themeDrawer.drawers[drawer.id]?.isOpen;
    }

    preparePreviewContent(previewContent) {
      const description = previewContent.querySelector('.product-recommendations-preview__description');
      // Description copy stays complete; page-wide styles/scripts must remain inert in a preview.
      description?.querySelectorAll('style, script, link[rel="stylesheet"], meta').forEach((node) => node.remove());
      // Only discard empty top-level spacer paragraphs, not meaningful copy or inline line breaks.
      description?.querySelectorAll(':scope > p').forEach((paragraph) => {
        if (!paragraph.children.length && !paragraph.textContent.trim()) paragraph.remove();
      });
      description?.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.decoding = 'async';
      });
      description?.querySelectorAll('table').forEach((table) => {
        const scroll = document.createElement('div');
        scroll.className = 'product-recommendations-preview__table-scroll';
        scroll.tabIndex = 0;
        scroll.setAttribute('role', 'region');
        scroll.setAttribute('aria-label', table.caption?.textContent.trim() || 'Product details table');
        table.before(scroll);
        scroll.append(table);
      });
    }

    async renderPreview(template, selection) {
      const drawer = this.previewDrawer;
      const body = drawer.querySelector('[data-preview-content]');
      const previewContent = template.content.cloneNode(true);
      const incoming = new URL(selection.href, window.location.origin);
      const productUrl = selection.productUrl;
      let selectedColor;
      previewContent.querySelectorAll('.product-item__swatch[href]').forEach((link) => {
        const button = document.createElement('button');
        Array.from(link.attributes).forEach(({ name, value }) => {
          if (name !== 'href') button.setAttribute(name, value);
        });
        button.type = 'button';
        button.dataset.previewUrl = this.previewUrl(link.href, productUrl);
        button.setAttribute('aria-pressed', 'false');
        button.append(...link.childNodes);
        const colorUrl = new URL(link.href, window.location.origin);
        if (selection.isSwatch && colorUrl.pathname === incoming.pathname
          && colorUrl.searchParams.get('variant') === incoming.searchParams.get('variant')
          && colorUrl.searchParams.get('option_values') === incoming.searchParams.get('option_values')) selectedColor = button;
        link.replaceWith(button);
      });
      const more = previewContent.querySelector('.product-item__swatch-more');
      if (more) {
        const note = document.createElement('p');
        note.className = 'product-recommendations-preview__summary';
        note.textContent = 'More colors are available on the product page.';
        more.replaceWith(note);
      }
      selectedColor ||= previewContent.querySelector('button[data-preview-url]');
      if (selectedColor) await this.selectPreviewColor(selectedColor, previewContent);
      if (!this.isCurrentPreview(selection, drawer)) return;
      body.replaceChildren(previewContent);
      body.scrollTop = 0;
      this.configurePreviewGallery();
    }

    adoptSectionStyles(html) {
      const styles = new Set(Array.from(document.querySelectorAll('style[data-section-stylesheet]'), (style) => style.textContent));
      const links = new Set(Array.from(document.querySelectorAll('link[rel="stylesheet"]'), (link) => link.href));
      html.querySelectorAll('style[data-section-stylesheet], link[rel="stylesheet"]').forEach((style) => {
        const known = style.tagName === 'LINK' ? links : styles;
        const key = style.tagName === 'LINK' ? style.href : style.textContent;
        if (known.has(key)) {
          style.remove();
        } else {
          known.add(key);
          document.head.append(style);
        }
      });
    }

    removeEmptyRecommendations() {
      this.remove();
    }

    showRecommendationsRetry() {
      this.dataset.recommendationsLoadFailed = 'true';
      this.innerHTML = '<button type="button" class="ds-btn ds-btn-secondary" data-product-recommendations-retry>Retry</button>';
      this.querySelector('[data-product-recommendations-retry]')?.addEventListener('click', () => {
        this.dataset.recommendationsLoadFailed = 'false';
        this.innerHTML = '';
        this.loadRecommendations();
      }, { once: true });
    }

    async loadRecommendations() {
      if (this.recommendationsLoading || this.dataset.recommendationsLoaded === 'true') return;
      this.recommendationsLoading = true;
      const request = this.recommendationsRequest = new AbortController();
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(this.dataset.url, { signal: request.signal });
        if (!response.ok) throw new Error(`Product recommendations request failed with status ${response.status}`);

        const markup = await response.text();
        if (request.signal.aborted || this.recommendationsRequest !== request || !this.isConnected) return;
        // Parse inertly: only the selected recommendation fragment may request images/mount elements.
        const html = document.createElement('template');
        html.innerHTML = markup;
        const recommendations = Array.from(html.content.querySelectorAll('product-recommendations'))
          .find((element) => !this.dataset.blockId || element.dataset.blockId === this.dataset.blockId);

        if (!recommendations?.innerHTML.trim()) {
          this.removeEmptyRecommendations();
          return;
        }

        // Partial HTML still needs styles collected outside the recommendations element.
        this.adoptSectionStyles(html.content);
        // Swatch variant links retain the API's recommendation attribution.
        recommendations.querySelectorAll('[data-product-card]').forEach((card) => {
          const productLink = card.querySelector('.product-item__name[href]');
          if (!productLink) return;
          const tracking = new URL(productLink.getAttribute('href'), window.location.origin).searchParams;
          card.querySelectorAll('.product-item__swatch[href], .product-item__swatch-more[href]').forEach((link) => {
            const url = new URL(link.getAttribute('href'), window.location.origin);
            tracking.forEach((value, key) => {
              if (key.startsWith('pr_')) url.searchParams.set(key, value);
            });
            link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
          });
        });
        this.innerHTML = recommendations.innerHTML;
        this.dataset.recommendationsLoaded = 'true';
        this.observeRecommendationSwatches();
        this.preparePreviewTriggers();
        window.productItemVideoReveal?.init(this);

        if (typeof BlsAnimations !== 'undefined') BlsAnimations.innit(this);
      } catch (error) {
        if (request.signal.aborted || this.recommendationsRequest !== request || !this.isConnected) return;
        console.error('Unable to load product recommendations', error);
        this.showRecommendationsRetry();
      } finally {
        if (this.recommendationsRequest === request) {
          this.recommendationsRequest = null;
          this.recommendationsLoading = false;
          this.removeAttribute('aria-busy');
        }
      }
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  }

  function scheduleAfterNextPaint(callback) {
    if (!('requestAnimationFrame' in window)) {
      window.setTimeout(callback, 0);
      return;
    }
    window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
  }

  function parseConfig(node) {
    try {
      return {
        sectionId: '',
        productFormId: '',
        productHandle: '',
        productTitle: '',
        productType: '',
        productTags: [],
        productTemplateSuffix: '',
        measurementHelpEnabled: false,
        isCafe: false,
        deferredCssUrl: '',
        measurementCssUrl: '',
        measurementJsUrl: '',
        ...JSON.parse(node.textContent || '{}'),
      };
    } catch (error) {
      console.error('Product integrations config is invalid', error);
      return {
        sectionId: '',
        productFormId: '',
        productHandle: '',
        productTitle: '',
        productType: '',
        productTags: [],
        productTemplateSuffix: '',
        measurementHelpEnabled: false,
        isCafe: false,
        deferredCssUrl: '',
        measurementCssUrl: '',
        measurementJsUrl: '',
      };
    }
  }

  function getRoot(config) {
    return document.getElementById(`MainProduct-${config.sectionId}`);
  }

  function once(element, key) {
    if (!element || !key) return false;
    const attr = `productIntegrations${key}`;
    if (element.dataset[attr] === 'true') return false;
    element.dataset[attr] = 'true';
    return true;
  }

  // Liquid decides whether OPTIS is required. Only wait for its widget and
  // intercepted purchase button; stop observing on readiness or timeout.
  function waitForProductOptions(root, timeout, config) {
    return new Promise((resolve, reject) => {
      let observer, timer;
      const cleanup = () => {
        observer?.disconnect();
        window.clearTimeout(timer);
      };
      const check = () => {
        const options = root.querySelector(PRODUCT_OPTIONS_SELECTOR);
        // 样式在插件 DOM 出现时就准备好，不等待加购拦截就绪才改变首帧布局。
        if (options) initProductOptionStyles(options, config);
        if (options && root.querySelector('.product_submit_button[po-btn-atc="true"]')) {
          cleanup();
          resolve(options);
          return true;
        }
        return false;
      };
      if (check()) return;
      observer = new MutationObserver(check);
      observer.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['po-btn-atc'],
      });
      timer = window.setTimeout(() => {
        cleanup();
        reject(new Error('Product options could not load. Refresh this page to try again.'));
      }, timeout);
    });
  }

  // This root owns the inventory/options gates; ProductForm owns aria-disabled
  // while submitting. Native variant handlers call this hook after updating stock.
  function initProductPurchaseState(root, config) {
    let available = config.selectedVariantAvailable === true;
    let optionsState = config.requiresProductOptions ? 'loading' : 'ready';
    const buttons = () => queryAll(root, '.product_submit_button, .sticky-shop-now-button');
    const sync = (variantAvailable) => {
      if (typeof variantAvailable === 'boolean') available = variantAvailable;
      root.dataset.productOptionsState = optionsState;
      const submitting = queryAll(root, '.product_submit_button')
        .some((button) => button.getAttribute('aria-disabled') === 'true');
      buttons().forEach((button) => {
        button.disabled = !available || optionsState !== 'ready' || submitting;
        const loadingOptions = available && optionsState === 'loading';
        button.classList.toggle('product-options-loading', loadingOptions);
        if (loadingOptions || submitting) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      });
    };
    root._syncProductViewPurchaseState = sync;
    // Enter/programmatic submit must obey the same gates as clicking a button.
    root.addEventListener('submit', (event) => {
      if (!event.target.matches('form[data-type="add-to-cart-form"]')) return;
      sync();
      if (!available || optionsState !== 'ready' || query(root, '.product_submit_button[aria-disabled="true"]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
    sync();
    return {
      ready() {
        optionsState = 'ready';
        sync();
      },
      fail(message) {
        if (!config.requiresProductOptions) return;
        optionsState = 'error';
        sync();
        const wrapper = query(root, '.product-form__error-message-wrapper');
        const messageNode = wrapper?.querySelector('.product-form__error-message');
        if (messageNode) {
          messageNode.textContent = message;
          wrapper.hidden = false;
        }
      },
    };
  }

  function query(root, selector) {
    return root.querySelector(selector);
  }

  function queryAll(root, selector) {
    return Array.from(root.querySelectorAll(selector));
  }

  function normalizeText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .replace(/[?？:：]+$/g, '')
      .trim()
      .toLowerCase();
  }

  function normalizeOptionName(value) {
    return normalizeText(value)
      .replace(/^po[_\s-]*/i, '')
      .replace(/\*/g, '')
      .trim();
  }

  function normalizeMatchKey(value) {
    return normalizeOptionName(value).replace(/[^a-z0-9]/g, '');
  }

  function normalizeList(value) {
    if (Array.isArray(value)) return value.map(normalizeMatchKey).filter(Boolean);
    if (value && typeof value === 'object') return Object.values(value).map(normalizeMatchKey).filter(Boolean);
    return String(value || '').split(',').map(normalizeMatchKey).filter(Boolean);
  }

  function isCurtainsProduct(config) {
    return normalizeList(config?.productTags).some((tag) => tag.includes('curtains'));
  }

  function isRodsAndTracksProduct(config) {
    return /\brods?\b/.test(normalizeText(config?.productType)) ||
      normalizeList(config?.productTags).includes('rodstracks');
  }

  let optionHelpConfigCache = null;
  function parseOptionHelpConfig() {
    if (optionHelpConfigCache?.node.isConnected) return optionHelpConfigCache;
    const node = document.querySelector(OPTION_HELP_SELECTOR);
    if (!node) return { groups: [] };

    try {
      const data = JSON.parse(node.textContent || '{}');
      const groups = Array.isArray(data.groups) ? data.groups : [];
      optionHelpConfigCache = {
        node,
        groups,
        entries: new Map(groups.map((group) => [group.helpKey, group])),
      };
      return optionHelpConfigCache;
    } catch (error) {
      console.error('Product option help config is invalid', error);
      return { groups: [] };
    }
  }

  function getOptionLabelElement(stack) {
    if (!stack) return null;
    return stack.querySelector('.po-label') || stack.querySelector('.po-label-container') || getSizeHeadingElement(stack);
  }

  function getSizeHeadingElement(stack) {
    const heading = stack.querySelector('.po-option--heading-container > h3');
    return heading && normalizeMatchKey(cleanOptionLabel(heading.textContent)) === 'size' ? heading : null;
  }

  function getOptionName(optionContainer) {
    const label = getOptionLabelElement(optionContainer.closest('.po-stack'));
    if (label) {
      const labelClone = label.cloneNode(true);
      // OPTIS 会在选中后向标题插入选中值和加价，它们不属于属性名称。
      labelClone.querySelectorAll('.po-required, .po-helptext, .po-helptext-content, .po-helptext-tooltip, .option-number, .po-selected-swatch, .po-extra-price').forEach((item) => item.remove());
      return labelClone.textContent || '';
    }

    const field = optionContainer.querySelector('[key]');
    return field ? field.getAttribute('key') || '' : '';
  }

  function getHelpTextName(candidate) {
    const textNode = candidate.matches('.po-helptext-tooltip-resize, .tooltip-text')
      ? candidate
      : candidate.querySelector('.po-helptext-tooltip-resize, .tooltip-text, .po-helptext-content');
    const cleanTextNode = textNode ? textNode.cloneNode(true) : candidate.cloneNode(true);
    cleanTextNode.querySelectorAll('[data-freshine-action], [data-product-option-action], [data-measurement-action]').forEach((item) => item.remove());
    const rawText = cleanTextNode.textContent;
    return String(rawText || '').replace(/\s+/g, ' ').trim();
  }

  function isHowToMeasureHelpText(candidate) {
    const helpKey = normalizeMatchKey(getHelpTextName(candidate));
    return helpKey === 'howtomeasure' || helpKey.includes('howtomeasure') || helpKey.includes('measurementguide');
  }

  function isSinglePanelSizeOption(optionName) {
    const optionKey = normalizeMatchKey(optionName);
    return optionKey.includes('size');
  }

  function getMeasurementToolAction(config) {
    const productTags = normalizeList(config?.productTags);
    const usesStandardMeasurementTool = isCurtainsProduct(config) || productTags.some((tag) => (
      tag.includes('shade') || tag.includes('blind')
    ));
    return usesStandardMeasurementTool ? 'measurement-tool' : 'measurement-roman';
  }

  function ensureMeasurementToolLink(candidate, optionName, config) {
    if (
      !candidate ||
      !config?.measurementHelpEnabled ||
      isRodsAndTracksProduct(config) ||
      !isSinglePanelSizeOption(optionName) ||
      !isHowToMeasureHelpText(candidate) ||
      candidate.querySelector('[data-freshine-action]')
    ) return;

    const link = document.createElement('a');
    link.href = '#';
    link.dataset.freshineAction = getMeasurementToolAction(config);
    link.className = 'option-help-measurement-tool-link';
    link.textContent = 'Use Measurement Tool →';
    link.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openMeasurementToolOverlay(e, config, link.dataset.freshineAction);
    });
    candidate.appendChild(document.createTextNode(' '));
    candidate.appendChild(link);
  }

  function findConfiguredSizeHelpText(stack, labelContainer) {
    if (!stack) return null;

    return Array.from(stack.querySelectorAll([
      '.po-helptext-content',
      '.po-helptext-tooltip',
      '.po-helptext',
      '.po-help-text',
      '.po-label-container',
    ].join(','))).find((item) => (
      item !== labelContainer &&
      !item.closest('[data-product-integrations-injected="size-helptext-link"]') &&
      !item.querySelector('.po-label') &&
      isHowToMeasureHelpText(item)
    )) || null;
  }

  function prepareConfiguredSizeHelpText(helpText, config) {
    if (!helpText) return false;

    helpText.classList.add('po-helptext-content', 'option-help-size-helptext', 'option-help-how-to-measure');
    ensureMeasurementToolLink(helpText, 'Size', config);
    return true;
  }

  function findOptionHelpGroup(optionHelpConfig, optionName, helpTextName) {
    // Liquid 已按入口、Type、tags、Priority 选出唯一 group，浏览器仅定位该入口。
    const helpKey = normalizeMatchKey(helpTextName);
    const optionKey = normalizeMatchKey(optionName);
    return optionHelpConfig.entries.get(helpKey) ||
      // 兼容 Motor Type、Remote 等仍以 OPTIS 选项标题配置的入口。
      optionHelpConfig.entries.get(optionKey) || null;
  }

  function createOptionHelpImages(item, imageUrls) {
    if (!Array.isArray(imageUrls) || !imageUrls.length) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'option-help-drawer__accordion-images';

    imageUrls.forEach((url) => {
      if (!url) return;
      const image = document.createElement('img');
      image.className = 'option-help-drawer__accordion-image';
      image.src = url;
      image.alt = item.helpTextName || item.title || '';
      image.loading = 'lazy';
      image.width = 600;
      image.height = 600;
      wrapper.appendChild(image);
    });

    return wrapper.childElementCount ? wrapper : null;
  }

  function createOptionHelpVideos(item, videos) {
    if (!Array.isArray(videos) || !videos.length) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'option-help-drawer__accordion-images option-help-drawer__accordion-images--video';

    videos.forEach((videoItem) => {
      const sources = videoItem && Array.isArray(videoItem.sources) ? videoItem.sources : [];
      if (!sources.length) return;

      const video = document.createElement('video');
      video.className = 'option-help-drawer__accordion-image option-help-drawer__accordion-video';
      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.width = 600;
      video.height = 600;
      video.setAttribute('aria-label', item.helpTextName || item.title || 'Help video');
      if (videoItem.poster) video.poster = videoItem.poster;

      sources.forEach((sourceItem) => {
        if (!sourceItem || !sourceItem.url) return;
        const source = document.createElement('source');
        source.src = sourceItem.url;
        if (sourceItem.type) source.type = sourceItem.type;
        video.appendChild(source);
      });

      if (video.childElementCount) wrapper.appendChild(video);
    });

    return wrapper.childElementCount ? wrapper : null;
  }

  function markLiveChatLinks(container) {
    if (!container) return;

    container.querySelectorAll('a[href]').forEach((link) => {
      const normalizedLinkText = normalizeMatchKey(link.textContent || '');
      const normalizedHref = normalizeMatchKey(link.getAttribute('href') || '');
      const normalizedTitle = normalizeMatchKey(link.getAttribute('title') || '');

      if (
        normalizedLinkText.includes('livechat') ||
        normalizedHref.includes('livechat') ||
        normalizedTitle.includes('livechat')
      ) {
        link.href = '#';
        link.dataset.openChat = 'true';
      }
    });
  }

  function createOptionHelpContent(item) {
    if (!item.content) return null;

    const itemContent = document.createElement('div');
    itemContent.className = 'theme-drawer__accordion-content option-help-drawer__accordion-content';
    itemContent.innerHTML = item.content;
    markLiveChatLinks(itemContent);
    return itemContent;
  }

  function appendOptionHelpStandaloneItem(content, item, imageUrls, videos) {
    const block = document.createElement('div');
    block.className = 'option-help-drawer__standalone';

    const videoList = createOptionHelpVideos(item, videos);
    if (videoList) {
      block.appendChild(videoList);
    } else {
      const images = createOptionHelpImages(item, imageUrls);
      if (images) block.appendChild(images);
    }

    const itemContent = createOptionHelpContent(item);
    if (itemContent) block.appendChild(itemContent);

    if (block.childNodes.length) content.appendChild(block);
  }

  function getOptionHelpAccordionPanel(accordion) {
    return accordion ? accordion.querySelector('.option-help-drawer__accordion-panel') : null;
  }

  function setOptionHelpAccordionState(accordion, isOpen) {
    const panel = getOptionHelpAccordionPanel(accordion);
    const heading = accordion ? accordion.querySelector('.option-help-drawer__accordion-heading') : null;

    if (!accordion || !panel) return;

    accordion.classList.toggle('active', isOpen);
    panel.style.display = isOpen ? 'block' : 'none';
    heading?.setAttribute('aria-expanded', String(isOpen));
  }

  function closeSiblingOptionHelpAccordions(accordion) {
    const content = accordion ? accordion.closest('.option-help-drawer__content') : null;
    if (!content) return;

    content.querySelectorAll('.option-help-drawer__accordion.active').forEach((openAccordion) => {
      if (openAccordion !== accordion) {
        setOptionHelpAccordionState(openAccordion, false);
      }
    });
  }

  function appendOptionHelpAccordionItem(content, item, imageUrls, videos) {
    const accordion = document.createElement('collapsible-block');
    accordion.className = 'theme-drawer__accordion option-help-drawer__accordion collection-filter__group border-bottom block pb-5';
    accordion.tabIndex = 0;
    accordion.dataset.contentItem = item.handle || '';

    const summary = document.createElement('h3');
    summary.className = 'collapsible-heading theme-drawer__accordion-heading option-help-drawer__accordion-heading collection-filter__heading w-full relative my-0 heading-letter-spacing h6';
    summary.setAttribute('role', 'button');
    summary.setAttribute('tabindex', '0');
    summary.setAttribute('aria-expanded', 'false');
    const heading = document.createElement('span');
    heading.className = 'theme-drawer__accordion-title option-help-drawer__accordion-title';
    heading.textContent = item.helpTextName || item.title || item.handle || '';
    summary.appendChild(heading);

    const toggle = document.createElement('span');
    toggle.className = 'open-children-toggle theme-drawer__accordion-toggle option-help-drawer__toggle collection-filter__toggle inline-flex align-center content-center pointer';
    toggle.setAttribute('aria-hidden', 'true');
    toggle.innerHTML = '<svg class="theme-drawer__accordion-arrow option-help-drawer__arrow collection-filter__arrow ds-icon-24 transition" width="24" height="24" viewBox="0 0 1024 1024" fill="none"><use href="#ic-arrow-down"></use></svg>';
    summary.appendChild(toggle);
    accordion.appendChild(summary);

    const panel = document.createElement('div');
    panel.className = 'collapsible-content theme-drawer__accordion-panel option-help-drawer__accordion-panel collection-filter__content filter-content custom-scrollbar pb-20 overflow-hidden';
    panel.style.display = 'none';

    const videoList = createOptionHelpVideos(item, videos);
    if (videoList) {
      panel.appendChild(videoList);
    } else {
      const images = createOptionHelpImages(item, imageUrls);
      if (images) panel.appendChild(images);
    }

    const itemContent = createOptionHelpContent(item);
    if (itemContent) panel.appendChild(itemContent);

    accordion.appendChild(panel);
    summary.addEventListener('click', (event) => {
      event.stopImmediatePropagation();
      const shouldOpen = !accordion.classList.contains('active');

      if (typeof slideAnime === 'function') {
        const started = slideAnime({
          target: panel,
          animeType: shouldOpen ? 'slideDown' : 'slideUp',
          addFunction: () => {
            summary.setAttribute('aria-expanded', String(shouldOpen));
          },
        });
        if (started === false) return;
      } else {
        panel.style.display = shouldOpen ? 'block' : 'none';
        summary.setAttribute('aria-expanded', String(shouldOpen));
      }
      accordion.classList.toggle('active', shouldOpen);
    });
    summary.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      summary.click();
    });
    content.appendChild(accordion);
  }

  function getOptionHelpImages(item, fallbackImages, index) {
    if (Array.isArray(item.images) && item.images.length) return item.images;
    if (item.image) return [item.image];
    const fallback = fallbackImages[index];
    return fallback ? [fallback] : [];
  }

  function getOptionHelpVideos(item) {
    return Array.isArray(item.videos) ? item.videos : [];
  }

  function getOptionHelpDrawerTitle(group, trigger) {
    return getHelpTextName(trigger) || group.items?.[0]?.handle || group.handle || group.title || group.optionName || '';
  }

  function getOptionHelpGroupForScope(optionHelpConfig, optionContainer, trigger) {
    return findOptionHelpGroup(
      optionHelpConfig,
      getOptionName(optionContainer),
      trigger ? getHelpTextName(trigger) : ''
    );
  }

  function getOptionScope(trigger) {
    return trigger.closest('.po-stack, .po-option--container');
  }

  function getOptionContainer(optionScope) {
    if (!optionScope) return null;
    return optionScope.matches('.po-option--container')
      ? optionScope
      : optionScope.querySelector('.po-option--container') || optionScope;
  }

  function isNativeHelpTextLinkClick(event, trigger) {
    const link = event.target.closest('a[href]');
    if (!link || (!trigger.contains(link) && !link.contains(trigger))) return false;
    if (getProductOptionActionFromLink(link)) return true;

    const href = link.getAttribute('href');
    return Boolean(href && href !== '#' && !href.toLowerCase().startsWith('javascript:'));
  }

  function hasProductOptionActionLink(element) {
    if (!element) return false;
    return Array.from(element.querySelectorAll('a[href], [data-freshine-action], [data-product-option-action], [data-measurement-action]'))
      .some((link) => Boolean(getProductOptionActionFromLink(link)));
  }

  function getProductOptionActionFromLink(link) {
    if (!link) return '';

    const action = normalizeMatchKey(
      link.dataset.freshineAction ||
      link.dataset.productOptionAction ||
      link.dataset.measurementAction ||
      ''
    );

    if (action === 'measurementroman' || action === 'helpmemeasure') return 'measurement-roman';
    if (action === 'measurementtool' || action === 'openmeasurementtool') return 'measurement-tool';
    if (action === 'howtomeasure' || action === 'measurementdrawer') return 'how-to-measure';

    const href = link.getAttribute('href') || '';
    const title = link.getAttribute('title') || '';
    const value = `${href} ${title} ${link.textContent || ''}`.toLowerCase();
    const normalized = normalizeMatchKey(value);

    if (
      value.includes('#how-to-measure') ||
      value.includes('/how-to-measure') ||
      normalized.includes('howtomeasure') ||
      normalized.includes('measurementguide')
    ) {
      return 'how-to-measure';
    }

    if (
      value.includes('#measurement-tool') ||
      value.includes('/measurement-tool') ||
      normalized.includes('measurementtool') ||
      normalized.includes('measuretool')
    ) {
      return 'measurement-tool';
    }

    return '';
  }

  function getMeasurementIframeUrl(action, config) {
    const measurementAction = action || getMeasurementToolAction(config);
    return measurementAction === 'measurement-tool' ? '/pages/measurement-tool' : '/pages/help-me-measure';
  }

  function normalizeIframeMeasurementResult(result) {
    if (!result) return null;

    const width = result.width || result.r_width;
    const height = result.height || result.r_height;
    if (!width || !height) return null;

    return {
      ...result,
      width,
      height,
      qty: result.qty || result.r_qty || result.quantity || 1,
      name: result.name || result.header || result.Header,
      width_F: result.width_F || result.r_width_F,
      height_F: result.height_F || result.r_height_F,
    };
  }

  function readIframeStorageResult(iframe, action) {
    try {
      const storageKey = action === 'measurement-tool' ? 'baseRoom' : 'baseRoomRoman';
      const rawResult = iframe.contentWindow?.sessionStorage?.getItem(storageKey);
      return normalizeIframeMeasurementResult(JSON.parse(rawResult || '{}'));
    } catch (error) {
      console.warn('Unable to read measurement iframe result', error);
      return null;
    }
  }

  function closeMeasurementIframe() {
    const overlay = document.querySelector('[data-product-measurement-iframe-overlay]');
    if (!overlay) return;
    overlay.hidden = true;
    const iframe = overlay.querySelector('iframe');
    if (iframe) iframe.src = 'about:blank';
  }

  function syncMeasurementIframeResult(iframe, action) {
    const result = readIframeStorageResult(iframe, action);
    if (!result) return false;

    const roomName = result.room_name || result.roomName || 'baseRoom';
    const resultWithRoom = {
      ...result,
      room_name: roomName,
    };

    localStorage.setItem('measurementProductResult', JSON.stringify(resultWithRoom));
    sessionStorage.setItem(action === 'measurement-tool' ? 'baseRoom' : 'baseRoomRoman', JSON.stringify({
      ...resultWithRoom,
      r_width: result.width,
      r_height: result.height,
      r_qty: result.qty,
    }));
    applyMeasurementResultToProduct(resultWithRoom);
    fillRoomNameWithRetry(roomName);
    closeMeasurementIframe();
    return true;
  }

  function bindMeasurementIframeResult(iframe, action) {
    try {
      const iframeDocument = iframe.contentDocument;
      if (!iframeDocument || iframe.dataset.productMeasurementIframeBound === 'true') return;
      iframe.dataset.productMeasurementIframeBound = 'true';

      iframeDocument.addEventListener('click', (event) => {
        if (!event.target.closest('.shop-btn')) return;
        window.setTimeout(() => syncMeasurementIframeResult(iframe, action), 150);
      }, true);
    } catch (error) {
      console.warn('Unable to bind measurement iframe result', error);
    }
  }

  function ensureMeasurementIframeOverlay() {
    let overlay = document.querySelector('[data-product-measurement-iframe-overlay]');
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.className = 'product-measurement-iframe';
    overlay.dataset.productMeasurementIframeOverlay = 'true';
    overlay.hidden = true;
    overlay.innerHTML = `
      <div class="product-measurement-iframe__dialog" role="dialog" aria-modal="true" aria-label="Measurement Tool">
        <button class="product-measurement-iframe__close" type="button" aria-label="Close measurement tool">&times;</button>
        <iframe class="product-measurement-iframe__frame" title="Measurement Tool" loading="lazy"></iframe>
      </div>
    `;
    overlay.querySelector('.product-measurement-iframe__close')?.addEventListener('click', closeMeasurementIframe);
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) closeMeasurementIframe();
    });
    document.body.appendChild(overlay);
    return overlay;
  }

  function openEmbeddedMeasurementToolOverlay() {
    const parentSec = document.querySelector('.parent-sec');
    if (!parentSec) return false;

    const maskParent = document.querySelector('.nyg-mask-box-parent');
    const maskBox = document.querySelector('.nyg-mask-box');

    parentSec.style.display = 'block';
    parentSec.style.visibility = 'visible';
    if (maskParent) maskParent.style.display = 'block';
    if (maskBox) maskBox.style.visibility = 'visible';
    bindMeasurementCloseButton();
    return true;
  }

  function openMeasurementToolOverlay(event, config, action) {
    event.preventDefault();
    event.stopPropagation();

    const measurementAction = action || getMeasurementToolAction(config);
    initMeasurementModal();
    Promise.all([loadDeferredProductStyles(), loadMeasurementToolAssets(config)]).then(() => {
      if (openEmbeddedMeasurementToolOverlay()) return;

      const overlay = ensureMeasurementIframeOverlay();
      const iframe = overlay.querySelector('iframe');
      if (!iframe) return;

      iframe.dataset.productMeasurementIframeBound = 'false';
      iframe.addEventListener('load', () => bindMeasurementIframeResult(iframe, measurementAction), { once: true });
      iframe.src = getMeasurementIframeUrl(measurementAction, config);
      overlay.hidden = false;
    });
    return true;
  }

  function openHowToMeasureDrawer(event, config) {
    event.preventDefault();
    event.stopPropagation();

    return openMeasurementToolOverlay(event, config, getMeasurementToolAction(config));
  }

  function initProductOptionActionLinks(root, config) {
    if (document.documentElement.dataset.productOptionActionLinksDelegated === 'true') return;
    document.documentElement.dataset.productOptionActionLinksDelegated = 'true';

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href], [data-freshine-action], [data-product-option-action], [data-measurement-action]');
      if (!link || (root && !root.contains(link))) return;

      const optionScope = link.closest(`${PRODUCT_OPTIONS_SELECTOR}, .po-stack, .po-option--container, .po-helptext-content`);
      if (!optionScope) return;

      const action = getProductOptionActionFromLink(link);
      if (!action) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (action === 'measurement-tool') {
        openMeasurementToolOverlay(event, config, action);
        return;
      }

      if (action === 'measurement-roman') {
        openMeasurementToolOverlay(event, config, action);
        return;
      }

      if (action === 'how-to-measure') {
        openHowToMeasureDrawer(event, config);
      }
    }, true);
  }

  function bindMeasurementCloseButton() {
    if (window.__measurementCloseBound) return;
    window.__measurementCloseBound = true;

    document.addEventListener('click', function (e) {
      const closeBtn = e.target.closest('.icon-close-box, .nyg-mask-inner-box .icon-close');
      if (!closeBtn) return;

      const parentSec = document.querySelector('.parent-sec');
      const maskParent = document.querySelector('.nyg-mask-box-parent');
      if (parentSec) {
        parentSec.style.display = 'none';
      }
      if (maskParent) {
        maskParent.style.display = 'none';
      }
    });
  }

  function isAccordionHelpItem(item) {
    return Boolean(String(item.helpTextName || '').trim());
  }

  function getOptionHelpGroupFromTrigger(trigger) {
    const optionHelpConfig = parseOptionHelpConfig();
    if (!optionHelpConfig.groups.length) return null;

    const optionScope = getOptionScope(trigger);
    if (!optionScope) return null;

    const optionContainer = getOptionContainer(optionScope);
    if (!optionContainer) return null;

    return getOptionHelpGroupForScope(optionHelpConfig, optionContainer, trigger);
  }

  function extractBackgroundImageUrl(value) {
    const match = String(value || '').match(/url\((['"]?)(.*?)\1\)/);
    return match ? match[2] : '';
  }

  function findOptionScopeBySource(trigger, source, sourceLabel) {
    const productOptionsRoot = trigger?.closest(PRODUCT_OPTIONS_SELECTOR) || document.querySelector(PRODUCT_OPTIONS_SELECTOR);
    const searchRoots = [productOptionsRoot, document].filter(Boolean);
    const sourceKey = normalizeMatchKey(source);
    const labelKey = normalizeMatchKey(sourceLabel);
    if (!sourceKey && !labelKey) return null;

    for (const searchRoot of searchRoots) {
      const stacks = Array.from(searchRoot.querySelectorAll('.po-stack, .po-option--container')).filter((item) => (
        !item.matches('.po-option--container') || !item.closest('.po-stack')
      ));

      for (const stack of stacks) {
        const fields = Array.from(stack.querySelectorAll('[key], [name]'));
        const hasMatchingField = fields.some((field) => {
          const key = normalizeMatchKey(field.getAttribute('key'));
          const name = normalizeMatchKey(field.getAttribute('name'));
          return (
            (sourceKey && (key === sourceKey || name === sourceKey || name === normalizeMatchKey(`properties[${source}]`))) ||
            (labelKey && (key === labelKey || name === labelKey || name === normalizeMatchKey(`properties[${sourceLabel}]`)))
          );
        });
        if (hasMatchingField) return stack;

        const label = cleanOptionLabel(stack.querySelector('.po-label')?.textContent);
        const stackLabelKey = normalizeMatchKey(label);
        if (
          (sourceKey && (stackLabelKey === sourceKey || stackLabelKey === normalizeMatchKey(String(source).replace(/^po_/i, '')))) ||
          (labelKey && stackLabelKey === labelKey)
        ) {
          return stack;
        }
      }
    }

    return null;
  }

  function getHelpIndexSourceScope(trigger) {
    const source = trigger?.dataset.helpIndexSource || '';
    const sourceLabel = trigger?.dataset.helpIndexSourceLabel || '';
    const declaredScope = findOptionScopeBySource(trigger, source, sourceLabel);
    if (declaredScope) return declaredScope;

    const helpKey = normalizeMatchKey(getHelpTextName(trigger));
    if (helpKey === 'howtomeasure' || helpKey.includes('howtomeasure') || helpKey.includes('measurementguide')) {
      return findOptionScopeBySource(trigger, 'po_Header', 'Header');
    }

    return null;
  }

  function getOptionScopeForHelpState(trigger) {
    return getHelpIndexSourceScope(trigger) || getOptionScope(trigger);
  }

  function getOptionFallbackImages(trigger) {
    const optionScope = getOptionScopeForHelpState(trigger);
    if (!optionScope) return [];

    return Array.from(optionScope.querySelectorAll('.po-option__image-value')).map((item) => (
      extractBackgroundImageUrl(item.style.backgroundImage || getComputedStyle(item).backgroundImage)
    )).filter(Boolean);
  }

  function getSelectedOptionImageIndex(trigger) {
    const optionScope = getOptionScopeForHelpState(trigger);
    if (!optionScope) return -1;

    const imageValues = Array.from(optionScope.querySelectorAll('.po-option__image-value'));
    if (!imageValues.length) return -1;

    return imageValues.findIndex((item) => {
      const swatch = item.closest('.po-option--swatch, .po-option__value, .po-option__swatch-wrapper');
      if (swatch) {
        return swatch.classList.contains('selected') ||
               swatch.classList.contains('active') ||
               swatch.querySelector('input:checked') !== null;
      }
      return item.classList.contains('selected') || item.classList.contains('active');
    });
  }

  function openOptionHelpDrawer(group, trigger) {
    const title = document.getElementById('option-help-drawer-title');
    const content = document.getElementById('option-help-drawer-content');

    if (!title || !content) return;

    const drawer = document.getElementById(OPTION_HELP_DRAWER_ID);
    if (drawer) {
      drawer.dataset.contentSection = group.handle;
      drawer.dataset.contentEvent = 'product_help_drawer_click';
    }

    const titleText = getOptionHelpDrawerTitle(group, trigger);
    const selectedIndex = trigger ? getSelectedOptionImageIndex(trigger) : -1;
    const targetIndex = selectedIndex >= 0 ? selectedIndex : 0;
    const drawerKey = [
      group.handle,
      group.optionName,
      titleText,
      trigger ? getHelpTextName(trigger) : '',
    ].map(normalizeMatchKey).join('|');
    const shouldRenderContent = content.dataset.optionHelpDrawerKey !== drawerKey;

    if (shouldRenderContent) {
      const fallbackImages = trigger ? getOptionFallbackImages(trigger) : [];
      title.textContent = titleText;
      content.innerHTML = '';
      content.dataset.optionHelpDrawerKey = drawerKey;

      (group.items || []).forEach((item, index) => {
        const itemImages = getOptionHelpImages(item, fallbackImages, index);
        const itemVideos = getOptionHelpVideos(item);
        if (isAccordionHelpItem(item)) {
          appendOptionHelpAccordionItem(content, item, itemImages, itemVideos);
          return;
        }
        appendOptionHelpStandaloneItem(content, item, itemImages, itemVideos);
      });

    }

    // 选项变化只切换展开项，保留同组内容和已加载的媒体节点。
    if (trigger && (shouldRenderContent || content.dataset.optionHelpSelectedIndex !== String(targetIndex))) {
      const accordions = content.querySelectorAll('.option-help-drawer__accordion');
      if (accordions[targetIndex]) {
        closeSiblingOptionHelpAccordions(accordions[targetIndex]);
        setOptionHelpAccordionState(accordions[targetIndex], true);
      }
    }
    content.dataset.optionHelpSelectedIndex = String(targetIndex);

    window.themePopup?.bindVideoLinks(content);
    loadDeferredProductStyles().then(() => {
      initOptionHelpDrawer();
      window.themeDrawer?.open(OPTION_HELP_DRAWER_ID);
    });
  }

  function optionHelpCandidates(optionContainer) {
    return Array.from(optionContainer.querySelectorAll([
      '.po-helptext-content',
      '.po-helptext-tooltip',
      '.po-helptext',
      '.po-help-text',
      '[class*="helptext"]',
      '[class*="help-text"]',
    ].join(','))).filter((item) => (
      !item.dataset.optionHelpBound &&
      !item.closest('[data-option-help-trigger]') &&
      item.classList.contains('po-helptext-content') &&
      !hasProductOptionActionLink(item) &&
      getHelpTextName(item)
    ));
  }

  function bindOptionHelpTriggers(root, config, productOptionsRoot) {
    queryAll(root, '.po-stack, .po-option--container').filter((optionScope) => (
      !optionScope.matches('.po-option--container') || !optionScope.closest('.po-stack')
    )).forEach((optionScope) => {
      const optionContainer = optionScope.matches('.po-option--container')
        ? optionScope
        : optionScope.querySelector('.po-option--container') || optionScope;
      const optionName = getOptionName(optionContainer);
      optionHelpCandidates(optionScope).forEach((candidate) => {
        ensureMeasurementToolLink(candidate, optionName, config);
        candidate.dataset.optionHelpBound = 'true';
        candidate.dataset.optionHelpTrigger = 'true';
        candidate.classList.add('option-help-trigger');
        if (!candidate.hasAttribute('role')) candidate.setAttribute('role', 'button');
        if (!candidate.hasAttribute('tabindex')) candidate.setAttribute('tabindex', '0');
      });
    });
  }

  function initOptionHelpDelegation() {
    if (document.documentElement.dataset.productOptionHelpDelegated === 'true') return;
    document.documentElement.dataset.productOptionHelpDelegated = 'true';

    const open = (event) => {
      const trigger = event.target.closest('.po-helptext-content');
      if (!trigger) return;
      if (hasProductOptionActionLink(trigger) && event.target.closest('a[href], [data-freshine-action], [data-product-option-action], [data-measurement-action]')) return;
      if (isNativeHelpTextLinkClick(event, trigger)) return;

      const group = getOptionHelpGroupFromTrigger(trigger);
      if (!group || !Array.isArray(group.items) || !group.items.length) return;

      event.preventDefault();
      event.stopPropagation();
      window.themeAnalytics?.track('product_help_click', {
        section_key: 'product_help', item_key: group.handle,
        item_label: group.optionName, action: 'open', click_target: 'help',
        source_product: location.pathname.split('/').filter(Boolean).pop(),
      });
      openOptionHelpDrawer(group, trigger);
    };

    document.addEventListener('click', open, true);
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      open(event);
    }, true);
  }

  function initOptionHelpDrawer() {
    if (document.documentElement.dataset.productOptionHelpDrawerBound === 'true') return;
    document.documentElement.dataset.productOptionHelpDrawerBound = 'true';

    window.themeDrawer?.register(OPTION_HELP_DRAWER_ID, { position: 'right' });

    const closeButton = document.getElementById('option-help-drawer-close');
    const overlay = document.getElementById(`${OPTION_HELP_DRAWER_ID}-overlay`);
    closeButton?.addEventListener('click', () => window.themeDrawer?.close(OPTION_HELP_DRAWER_ID));
    overlay?.addEventListener('click', () => window.themeDrawer?.close(OPTION_HELP_DRAWER_ID));
  }

  function initSwatchDrawerDelegation() {
    if (document.documentElement.dataset.productIntegrationsSwatchDrawerDelegated === 'true') return;
    document.documentElement.dataset.productIntegrationsSwatchDrawerDelegated = 'true';

    document.addEventListener('click', (event) => {
      const target = event.target.closest('#Order-Fabric-Swatches, .order-fabric-swatches-trigger, .order-lining-swatches-trigger, .order-swatched-button');
      if (!target) return;
      event.preventDefault();
      const productRoot = target.closest('[id^="MainProduct-"]');
      const source = target.matches('.order-lining-swatches-trigger') ? 'lining' : 'fabric';
      mountOrderment(productRoot, source);
      window.dispatchEvent(new CustomEvent('open-swatch-drawer', { detail: { source } }));
      if (window.themeDrawer && document.getElementById('swatch-drawer-cart')) {
        window.themeDrawer.open('swatch-drawer-cart');
      }
    });
  }

  function mountOrderment(root, source) {
    if (!root) return null;
    const existing = root.querySelector('[data-orderment-root]');
    if (existing) {
      existing.dataset.swatchSource = source;
      return existing;
    }

    const template = root.querySelector('template[data-orderment-template]');
    if (!template) return null;
    const fragment = template.content.cloneNode(true);
    const mountedRoot = fragment.querySelector('[data-orderment-root]');
    if (mountedRoot) mountedRoot.dataset.swatchSource = source;
    template.replaceWith(fragment);
    return mountedRoot;
  }

  function isPriceContainerVisible(item) {
    // checkVisibility() answers "is this rendered" without forcing the
    // synchronous layout that offsetWidth triggers inside interaction-time
    // render passes; fall back for browsers without support.
    if (typeof item.checkVisibility === 'function') return item.checkVisibility();
    return item.offsetWidth > 0;
  }

  function placeReviewBadge(root) {
    const visiblePrice = queryAll(root, '.product-detail__price').find(isPriceContainerVisible);
    if (!visiblePrice) return false;

    // Judge.me outputs this badge in the HTML, before its optional --with-link class exists.
    const badge = REVIEW_SELECTORS.map((selector) => root.querySelector(selector)).find(Boolean);
    if (!badge) return false;

    const badgeRoot = badge.closest('[id*="judge_me_reviews"]') || badge.closest('.jdgm-widget') || badge;
    
    // 查找价格区域的 review slot
    const priceSlot = visiblePrice.querySelector('.product-view-review-slot');
    const target = priceSlot || visiblePrice;

    if (!target) return false;
    if (!target.contains(badgeRoot)) target.appendChild(badgeRoot);
    badgeRoot.style.display = 'inline-flex';
    badgeRoot.classList.add('product-view-review-badge--moved');
    return true;
  }

  function initReviews(root) {
    // Move the existing badge to the summary that becomes visible on resize/rotation.
    window.matchMedia('(min-width: 1024px)').addEventListener('change', () => placeReviewBadge(root));
    if (placeReviewBadge(root)) return;

    // Only late app insertion needs observation. Stop as soon as the badge is placed.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (REVIEW_SELECTORS.some((selector) => node.matches?.(selector) || node.querySelector?.(selector))) {
            if (placeReviewBadge(root)) {
              observer.disconnect();
              window.clearTimeout(stopTimer);
            }
            return;
          }
        }
      }
    });
    observer.observe(root, { childList: true, subtree: true });
    const stopTimer = window.setTimeout(() => observer.disconnect(), 6000);
  }

  function cleanOptionLabel(value) {
    return String(value || '')
      .replace(/^\s*\d+(?:\.\s*|\s+)/, '')
      .replace(/^\s*\d+(?=[A-Z][a-z])/, '')
      .replace(/\*/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getOptionStackName(stack) {
    const field = stack.querySelector('[key], [name]');
    const label = getOptionLabelElement(stack);
    return cleanOptionLabel(
      field?.getAttribute('key') ||
      field?.getAttribute('name') ||
      label?.textContent ||
      ''
    ).replace(/^po[_\s-]*/i, '');
  }

  function isSizeOptionStack(stack) {
    const optionKey = normalizeMatchKey(getOptionStackName(stack));
    return optionKey === 'size' || optionKey === 'singlepanelsize';
  }

  function shouldSkipOptionNumber(stack, hasSizeOption) {
    if (!hasSizeOption) return false;
    const optionKey = normalizeMatchKey(getOptionStackName(stack));
    return [
      'width',
      'widthinch',
      'widthinches',
      'widthfraction',
      'height',
      'heightinch',
      'heightinches',
      'heightfraction',
    ].includes(optionKey) || /^(?:width|height|corneranglewidth)[a-z](?:inch(?:es)?|fraction)?$/.test(optionKey);
  }

  function initNativeVariantSelection(root) {
    if (!once(root, 'NativeVariantSelection')) return;
    ensureNativeVariantOptionNumber(root, 1);

    const syncSelectedValue = (input) => {
      const selectedValue = input.closest('fieldset')?.querySelector('.option_value');
      if (selectedValue && selectedValue.textContent !== input.value) {
        selectedValue.textContent = input.value;
      }
    };
    const inputSelector = 'variant-radios-detail .product-form__input_color input[type="radio"]';
    root.addEventListener('change', (event) => {
      const input = event.target.closest(inputSelector);
      if (input) syncSelectedValue(input);
    });

    // 弱网下原生 radio 可能先于主题脚本响应，初始化时补齐已选颜色。
    queryAll(root, `${inputSelector}:checked`).forEach(syncSelectedValue);
    customElements.whenDefined('variant-radios-detail').then(() => {
      if (!root.isConnected) return;
      queryAll(root, 'variant-radios-detail').forEach((picker) => {
        if (picker.currentVariant) return;
        const earlySelection = queryAll(picker, '.product-form__input_color input:checked')
          .find((input) => !input.defaultChecked);
        // 只恢复脚本加载前遗漏的选择，复用既有变体、价格和插件同步链。
        earlySelection?.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function ensureNativeVariantOptionNumber(root, index) {
    const fieldsets = root?.querySelectorAll('variant-radios-detail .product-form__input_color') || [];
    let normalized = false;

    fieldsets.forEach((fieldset) => {
      const optionLabel = fieldset.querySelector('.form__label > span, .form__label.option_drop_value');
      if (!optionLabel) return;

      normalizeProductOptionLabel(optionLabel);
      const existingBadge = optionLabel.querySelector('.option-number');
      fieldset.dataset.optionNumber = String(index);
      const badge = existingBadge || document.createElement('span');
      badge.className = 'option-number';
      badge.textContent = String(index);
      badge.setAttribute('aria-hidden', 'true');
      badge.classList.toggle('is-selected', optionStackHasSelection(fieldset));
      if (!existingBadge) optionLabel.insertAdjacentElement('afterbegin', badge);
      normalized = true;
    });

    return normalized;
  }

  function normalizeProductOptionLabel(label) {
    if (!label) return;

    label.querySelectorAll('.po-required').forEach((item) => item.remove());
    label.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && /\*/.test(node.textContent)) {
        const cleanedText = node.textContent.replace(/\s*\*\s*/g, ' ');
        if (node.textContent !== cleanedText) node.textContent = cleanedText;
      }
    });
    label.querySelectorAll('span').forEach((item) => {
      if (item.classList.contains('option-number')) return;
      if (!/\*/.test(item.textContent || '')) return;
      if (item.children.length === 0) {
        item.textContent = item.textContent.replace(/\s*\*\s*/g, ' ').trim();
      }
    });
  }

  function isPlaceholderOptionValue(value) {
    const optionKey = normalizeMatchKey(value);
    return !optionKey || optionKey === 'pleaseselect' || optionKey === 'select' || optionKey.startsWith('select');
  }

  function optionStackHasSelection(stack) {
    if (stack.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) return true;

    const selectedOption = Array.from(stack.querySelectorAll('select')).some((select) => !isPlaceholderOptionValue(select.value));
    if (selectedOption) return true;

    const hasTextValue = Array.from(stack.querySelectorAll('input[type="text"], textarea')).some((input) => String(input.value || '').trim());
    if (hasTextValue) return true;

    return Boolean(stack.querySelector(
      '.po-option__value.selected, .po-option__value.active, .po-option--swatch.selected, .po-option--swatch.active, .po-option__swatch-wrapper.selected, .po-option__swatch-wrapper.active'
    ));
  }

  function isOptionStackVisible(stack) {
    if (stack.hidden || stack.getAttribute('aria-hidden') === 'true') return false;
    if (stack.style.display === 'none' || stack.style.visibility === 'hidden') return false;
    return true;
  }

  function getConditionalOptionGroups(searchRoot, optionStacks) {
    const groups = new Map();
    const stacks = optionStacks || Array.from(searchRoot.querySelectorAll('.po-stack'));
    const hasSizeOption = stacks.some(isSizeOptionStack);
    stacks.forEach((stack) => {
      if (!getOptionLabelElement(stack)) return;
      // Size 下的尺寸字段保留完整名称，不合并为条件选项标题，也不恢复子序号。
      if (shouldSkipOptionNumber(stack, hasSizeOption)) return;
      const optionName = getOptionStackName(stack).trim();
      const separatorIndex = optionName.search(CONDITIONAL_OPTION_LABEL_SEPARATOR);
      const baseName = (separatorIndex < 0 ? optionName : optionName.slice(0, separatorIndex)).trim();
      if (!optionName || !baseName) return;
      const key = normalizeMatchKey(baseName);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ stack, optionName, baseName });
    });

    const stackGroups = new Map();
    groups.forEach((items, key) => {
      const hasBaseOption = items.some(({ optionName, baseName }) => normalizeMatchKey(optionName) === normalizeMatchKey(baseName));
      const hasConditionalOption = items.some(({ optionName }) => optionName.search(CONDITIONAL_OPTION_LABEL_SEPARATOR) >= 0);
      if (!hasBaseOption || !hasConditionalOption) return;
      items.forEach(({ stack, baseName }) => stackGroups.set(stack, {
        key,
        baseName,
      }));
    });
    return stackGroups;
  }

  // 记录已完成编号的 root，Optis 条件展示切换时不重新编号
  const numberedRoots = new Set();
  const conditionalOptionNumbersByRoot = new WeakMap();

  function applyOptionNumbers(root, productOptionsRoot) {
    const key = productOptionsRoot || root || document;
    const searchRoot = productOptionsRoot || root || document;
    if (numberedRoots.has(key)) {
      return false;
    }

    const allStacks = Array.from(searchRoot.querySelectorAll('.po-stack'));
    allStacks.forEach((stack) => {
      if (getSizeHeadingElement(stack)) stack.dataset.productSizeHeading = 'true';
    });
    let stacks = allStacks
      .filter((stack) => getOptionLabelElement(stack))
      .filter(isOptionStackVisible);
    const sizeSearchRoot = searchRoot;
    const conditionalOptionGroups = getConditionalOptionGroups(sizeSearchRoot, allStacks);
    const conditionalOptionNumbers = conditionalOptionNumbersByRoot.get(key) || new Map();
    const hasSizeOption = allStacks
      .filter((stack) => getOptionLabelElement(stack))
      .some(isSizeOptionStack);

    const hasNativeColorOption = ensureNativeVariantOptionNumber(root, 1);
    if (!stacks.length && !hasNativeColorOption) return false;
    
    // 分组：color 选项单独编号，其他选项从 2 开始
    const colorStacks = [];
    const otherStacks = [];
    
    stacks.forEach((stack) => {
      const optionName = normalizeMatchKey(getOptionStackName(stack));
      const isColor = optionName === 'color' || optionName === 'colour' || optionName.includes('color');
      if (isColor) {
        if (!hasNativeColorOption) colorStacks.push(stack);
      } else {
        otherStacks.push(stack);
      }
    });

    // 为 color 选项添加编号的辅助函数
    function addNumberToColorStack(stack, index) {
      const label = getOptionLabelElement(stack);
      if (!label) return;
      normalizeProductOptionLabel(label);
      const existingBadge = label.querySelector('.option-number');
      stack.dataset.optionNumber = String(index);
      const badge = existingBadge || document.createElement('span');
      badge.className = 'option-number';
      badge.textContent = String(index);
      badge.setAttribute('aria-hidden', 'true');
      badge.classList.toggle('is-selected', optionStackHasSelection(stack));
      if (!existingBadge) label.insertAdjacentElement('afterbegin', badge);
    }

    // Color 选项编号从 1 开始
    let colorIndex = hasNativeColorOption ? 1 : 0;
    colorStacks.forEach((stack) => {
      colorIndex += 1;
      addNumberToColorStack(stack, colorIndex);
    });

    // Other options continue after Color. When a Color option is absent, start at 1.
    let optionIndex = colorIndex;
    otherStacks.forEach((stack) => {
      const label = getOptionLabelElement(stack);
      if (!label) return;
      normalizeProductOptionLabel(label);

      const existingBadge = label.querySelector('.option-number');
      if (shouldSkipOptionNumber(stack, hasSizeOption)) {
        existingBadge?.remove();
        stack.removeAttribute('data-option-number');
        return;
      }

      const conditionalGroup = conditionalOptionGroups.get(stack);
      let assignedNumber = conditionalGroup && conditionalOptionNumbers.get(conditionalGroup.key);
      if (!assignedNumber) {
        optionIndex += 1;
        assignedNumber = optionIndex;
        if (conditionalGroup) conditionalOptionNumbers.set(conditionalGroup.key, assignedNumber);
      }
      stack.dataset.optionNumber = String(assignedNumber);

      const badge = existingBadge || document.createElement('span');
      badge.className = 'option-number';
      badge.textContent = String(assignedNumber);
      badge.setAttribute('aria-hidden', 'true');
      badge.classList.toggle('is-selected', optionStackHasSelection(stack));
      if (!existingBadge) label.insertAdjacentElement('afterbegin', badge);
    });

    conditionalOptionNumbersByRoot.set(key, conditionalOptionNumbers);
    numberedRoots.add(key);
    return optionIndex > 0 || colorIndex > 0;
  }

  const optionDecorationWatchers = new WeakMap();

  function syncLiningSwatchTrigger(scope, config) {
    if (!config?.liningSwatchesEnabled) return;
    const stacks = scope.matches('.po-stack') ? [scope] : queryAll(scope, '.po-stack');
    stacks.forEach((stack) => {
      const optionContainer = stack.querySelector('.po-option--container') || stack;
      const existing = stack.querySelector(':scope > .order-lining-swatches-trigger');
      if (normalizeMatchKey(getOptionName(optionContainer)) !== 'liningtype') {
        existing?.remove();
        return;
      }
      if (existing) return;
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'order-swatched-question-button underline order-lining-swatches-trigger';
      trigger.textContent = 'Order Lining Swatches >>';
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', 'swatch-drawer-cart-panel');
      stack.appendChild(trigger);
    });
  }

  function initProductOptionStyles(productOptionsRoot, config) {
    if (!once(productOptionsRoot, 'OptionStyles')) return;
    // 这些标记只描述 DOM 结构，不接管 OPTIS 的选中、显隐或表单事件。
    // 缓存关系选择器，避免插件切换控件时反复使整组选项的 :has() 失效。
    const rules = [
      ['.po-label', 'data-product-numbered', '.option-number'],
      ['.po-label-container', 'data-product-numbered', ':scope > .option-number'],
      ['.po-label', 'data-product-selected-label', '.po-selected-swatch'],
      ['.po-option--instruction-container', 'data-product-size-help', '.option-help-size-row'],
      ['.po-stack', 'data-product-size-help', ':scope > .po-option--instruction-container .option-help-size-row'],
      ['.po-stack, .po-option--container', 'data-product-text-input', 'input.po-option--type-text, input[type="text"]'],
      ['.po-stack, .po-option--container', 'data-product-mount-help', '[key="po_Mount Type"]'],
    ];
    const sync = (scope) => {
      rules.forEach(([selector, attribute, descendant]) => {
        const nodes = Array.from(scope.querySelectorAll(selector));
        if (scope.matches(selector)) nodes.unshift(scope);
        nodes.forEach((node) => {
          const matches = Boolean(node.querySelector(descendant));
          if (node.hasAttribute(attribute) !== matches) node.toggleAttribute(attribute, matches);
        });
      });
      syncLiningSwatchTrigger(scope, config);
    };
    sync(productOptionsRoot);
    const observer = new MutationObserver((mutations) => {
      if (!productOptionsRoot.isConnected) { observer.disconnect(); return; }
      const scopes = new Set();
      mutations.forEach((mutation) => {
        // 文本、选中 class 和 style 的变化不改变上述结构；不观察属性，
        // 也不因主题写入标记或价格文本而再次扫描。
        const elements = [...mutation.addedNodes, ...mutation.removedNodes].filter((node) => node.nodeType === Node.ELEMENT_NODE);
        if (!elements.length) return;
        const scope = mutation.target.closest('.po-stack, .po-option--container');
        if (scope && productOptionsRoot.contains(scope)) {
          scopes.add(scope.closest('.po-stack') || scope);
        } else {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) scopes.add(node);
          });
        }
      });
      scopes.forEach((scope) => { if (scope.isConnected) sync(scope); });
    });
    observer.observe(productOptionsRoot, { childList: true, subtree: true });
    productOptionsRoot.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => observer.disconnect(), { once: true });
  }

  function waitForOptionDecoration(productOptionsRoot, callback) {
    if (callback()) return;

    let state = optionDecorationWatchers.get(productOptionsRoot);
    if (!state) {
      state = {
        callbacks: new Set(),
        frame: null,
        observer: null,
        timeout: null,
      };
      const disconnect = () => {
        state.observer?.disconnect();
        if (state.frame !== null) window.cancelAnimationFrame(state.frame);
        window.clearTimeout(state.timeout);
        optionDecorationWatchers.delete(productOptionsRoot);
      };
      const runCallbacks = () => {
        state.frame = null;
        state.callbacks.forEach((pendingCallback) => {
          if (pendingCallback()) state.callbacks.delete(pendingCallback);
        });
        if (!state.callbacks.size) disconnect();
      };
      state.observer = new MutationObserver((mutations) => {
        const hasOptionContent = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
          node.nodeType === Node.ELEMENT_NODE &&
          (
            node.matches?.('.po-stack, .po-option--container, .po-label, .po-label-container, .po-helptext-content, .po-helptext-tooltip, .po-option--swatch-container') ||
            node.querySelector?.('.po-stack, .po-option--container, .po-label, .po-label-container, .po-helptext-content, .po-helptext-tooltip, .po-option--swatch-container')
          )
        )));
        if (!hasOptionContent || state.frame !== null) return;
        state.frame = window.requestAnimationFrame(runCallbacks);
      });
      state.observer.observe(productOptionsRoot, { childList: true, subtree: true });
      state.timeout = window.setTimeout(disconnect, OPTION_DECORATION_OBSERVE_MS);
      optionDecorationWatchers.set(productOptionsRoot, state);
    }

    state.callbacks.add(callback);
  }

  function initOptionNumbers(root, productOptionsRoot) {
    if (!productOptionsRoot) return;
    waitForOptionDecoration(productOptionsRoot, () => applyOptionNumbers(root, productOptionsRoot));
  }

  function normalizeConditionalOptionLabel(stack, conditionalGroup) {
    const label = getOptionLabelElement(stack);
    const displayName = conditionalGroup?.baseName;
    if (!displayName) return false;
    const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();
    while (textNode) {
      if (normalizeMatchKey(textNode.textContent).startsWith(normalizeMatchKey(displayName))) {
        textNode.textContent = displayName;
        break;
      }
      textNode = walker.nextNode();
    }
    return true;
  }

  function syncConditionalOptionGroups(root, productOptionsRoot, groups) {
    const optionGroups = groups || getConditionalOptionGroups(productOptionsRoot);
    const numbers = conditionalOptionNumbersByRoot.get(productOptionsRoot);
    optionGroups.forEach((group, stack) => {
      const label = getOptionLabelElement(stack);
      if (!label) return;
      normalizeConditionalOptionLabel(stack, group);
      const number = numbers?.get(group.key);
      if (!number) return;
      stack.dataset.optionNumber = String(number);
      const existingBadge = label.querySelector('.option-number');
      const badge = existingBadge || document.createElement('span');
      badge.className = 'option-number';
      badge.textContent = String(number);
      badge.setAttribute('aria-hidden', 'true');
      badge.classList.toggle('is-selected', optionStackHasSelection(stack));
      if (!existingBadge) label.insertAdjacentElement('afterbegin', badge);
    });
  }

  function initConditionalOptionGroups(root, productOptionsRoot) {
    if (!once(root, 'ConditionalOptionGroups')) return;
    let pendingFrame = null;
    let groups = getConditionalOptionGroups(productOptionsRoot);
    let groupsStale = false;
    const syncGroups = () => {
      pendingFrame = null;
      if (groupsStale) {
        groups = getConditionalOptionGroups(productOptionsRoot);
        groupsStale = false;
      }
      syncConditionalOptionGroups(root, productOptionsRoot, groups);
    };
    const scheduleSync = () => {
      if (pendingFrame !== null) return;
      pendingFrame = window.requestAnimationFrame(syncGroups);
    };
    const observer = new MutationObserver((mutations) => {
      const hasRelevantChange = mutations.some((mutation) => {
        const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
        if (mutation.type === 'attributes') {
          return Boolean(target?.matches('.po-stack, .po-option--container'));
        }

        return Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          const addedStack = node.matches('.po-stack') || node.querySelector?.('.po-stack');
          if (addedStack) {
            groupsStale = true;
            return true;
          }
          return node.matches('.po-label, .po-label-container') || Boolean(node.querySelector?.('.po-label, .po-label-container'));
        });
      });
      if (hasRelevantChange) scheduleSync();
    });
    observer.observe(productOptionsRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
    productOptionsRoot.addEventListener('change', scheduleSync);
    scheduleSync();
    window.setTimeout(() => observer.disconnect(), OPTION_DECORATION_OBSERVE_MS);
  }

  function getOptisPropertyName(field, stack) {
    const name = String(field.name || '').trim();
    if (/^properties\[[^\]]+\]$/.test(name)) return name;
    const key = String(field.getAttribute('key') || '').trim();
    if (key.startsWith('po_')) return `properties[${key.slice(3)}]`;
    const stackName = stack ? getOptionStackName(stack) : '';
    return stackName ? `properties[${stackName}]` : '';
  }

  function isSuccessfulOptisField(field, stack, stackVisibility) {
    if (field.disabled || !field.value) return false;
    if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return false;
    if (field.tagName === 'SELECT' && isPlaceholderOptionValue(field.value || field.options[field.selectedIndex]?.textContent)) return false;
    return Boolean(stack && stackVisibility.get(stack));
  }

  function initOptisPropertySubmission(root, config, productOptionsRoot) {
    if (!once(root, 'OptisPropertySubmission')) return;
    const form = document.getElementById(config.productFormId);
    if (!form) return;

    form.addEventListener('formdata', (event) => {
      const fieldEntries = [];
      const stacks = new Set();
      productOptionsRoot.querySelectorAll('input, select, textarea').forEach((field) => {
        const stack = field.closest('.po-stack');
        if (stack) stacks.add(stack);
        const propertyName = getOptisPropertyName(field, stack);
        if (propertyName) fieldEntries.push({ field, propertyName, stack });
      });
      const stackVisibility = new Map();

      // Read the current state once per option stack before mutating FormData.
      // Values are intentionally never cached, so a change immediately before
      // Add to Cart is represented by the live DOM state.
      stacks.forEach((stack) => {
        if (stack.hidden || stack.getAttribute('aria-hidden') === 'true') {
          stackVisibility.set(stack, false);
          return;
        }
        const style = window.getComputedStyle(stack);
        stackVisibility.set(stack, style.display !== 'none' && style.visibility !== 'hidden');
      });

      // Rebuild only the BSS-owned keys. This removes stale native entries for
      // fields that became hidden immediately before submit while preserving
      // all non-BSS product form data.
      const managedNames = new Set();
      fieldEntries.forEach(({ field, propertyName }) => {
        if (field.name) managedNames.add(field.name);
        managedNames.add(propertyName);
      });
      managedNames.forEach((name) => event.formData.delete(name));

      fieldEntries.forEach(({ field, propertyName, stack }) => {
        if (!isSuccessfulOptisField(field, stack, stackVisibility)) return;
        event.formData.append(propertyName, field.value);
      });
    });
  }

  function insertSizeHelpTextLink(root, config) {
    const sizeLabel = queryAll(root, '.po-label, .po-label span').find((item) => {
      const text = cleanOptionLabel(item.textContent);
      return isSinglePanelSizeOption(text);
    });
    if (!sizeLabel) return false;

    const labelContainer = sizeLabel.closest('.po-label-container');
    if (!labelContainer) return false;

    const stack = labelContainer.closest('.po-stack');
    const configuredHelpText = findConfiguredSizeHelpText(stack, labelContainer);
    if (configuredHelpText) {
      stack.querySelectorAll('[data-product-integrations-injected="size-helptext-link"]').forEach((item) => item.remove());
      const prepared = prepareConfiguredSizeHelpText(configuredHelpText, config);
      if (prepared && config.measurementHelpEnabled && isCurtainsProduct(config)) {
        window.setTimeout(() => updateAllCurtainWidthTips(root), 0);
      }
      return prepared;
    }

    const injectedRow = stack
      ? stack.querySelector('[data-product-integrations-injected="size-helptext-link"]')
      : query(root, '[data-product-integrations-injected="size-helptext-link"]');
    if (injectedRow) {
      if (config.measurementHelpEnabled && isCurtainsProduct(config)) {
        window.setTimeout(() => updateAllCurtainWidthTips(root), 0);
      }
      return true;
    }
    if (stack && stack.querySelector('.po-helptext-content, .po-helptext-tooltip')) {
      if (config.measurementHelpEnabled && isCurtainsProduct(config)) {
        window.setTimeout(() => updateAllCurtainWidthTips(root), 0);
      }
      return true;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'option-help-size-row';
    wrapper.dataset.productIntegrationsInjected = 'size-helptext-link';

    const helpText = document.createElement('div');
    helpText.className = 'po-helptext-content option-help-size-helptext';
    helpText.textContent = 'How to Measure?';
    ensureMeasurementToolLink(helpText, 'Size', config);

    wrapper.appendChild(helpText);
    labelContainer.insertAdjacentElement('afterend', wrapper);
    if (config.measurementHelpEnabled && isCurtainsProduct(config)) {
      window.setTimeout(() => updateAllCurtainWidthTips(root), 0);
    }
    return true;
  }

  function initSizeHelpTextLink(root, config, productOptionsRoot) {
    waitForOptionDecoration(productOptionsRoot, () => insertSizeHelpTextLink(root, config));
  }

  function getSelectedCurtainWidth(root) {
    const selectors = [
      'select[key="po_Single Panel Width (inch)"]',
      'select[key="po_Single Panel – Finished Coverage Width (inch)"]',
      'select[name="Single Panel Width Before Pleats Made (inch)"]',
      'select[name="Single Panel Width (inch)"]',
      'select[key="po_Width"]',
      'select[key="po_Width (inch)"]',
      'select[key="po_Width(inch)"]',
      'select[name="Width"]',
      'select[name="Width (inch)"]',
      'select[name="Width(inch)"]',
    ];
    const scopes = [root];
    let sibling = root.nextElementSibling;
    while (sibling) {
      if (sibling.matches?.('.po-stack')) {
        const siblingKey = normalizeMatchKey(getOptionStackName(sibling));
        if (siblingKey.includes('height')) break;
        scopes.push(sibling);
        if (siblingKey.includes('width') && !siblingKey.includes('fraction')) break;
      }
      sibling = sibling.nextElementSibling;
    }

    const fields = [];
    scopes.forEach((scope) => {
      selectors.forEach((selector) => {
        const select = scope.querySelector(selector);
        if (select && !fields.includes(select)) fields.push(select);
      });
    });

    scopes.forEach((scope) => Array.from(scope.querySelectorAll('select')).forEach((select) => {
      const stack = select.closest('.po-stack');
      const fieldKey = normalizeMatchKey(
        select.getAttribute('key') ||
        select.getAttribute('name') ||
        (stack ? getOptionStackName(stack) : '')
      );
      if (
        fieldKey.includes('width') &&
        !fieldKey.includes('fraction') &&
        !fieldKey.includes('height') &&
        !fields.includes(select)
      ) {
        fields.push(select);
      }
    }));

    const widthScope = scopes.find((scope) => {
      const scopeKey = scope.matches?.('.po-stack') ? normalizeMatchKey(getOptionStackName(scope)) : '';
      return scopeKey.includes('width') && !scopeKey.includes('height') && !scopeKey.includes('fraction');
    }) || scopes.find((scope) => scope.querySelector('select'));
    const selectedDropdownOption = widthScope?.querySelector(
      '.po-dropdown-option.selected, .po-dropdown-option.active, .po-dropdown-option.is-selected, .po-dropdown-option[aria-selected="true"], .po-dropdown-option[data-selected="true"]'
    );
    const visibleValue = widthScope?.querySelector(
      '.po-value, .swatch_value, .swatch-value, [data-swatch-value], .po-dropdown-selected-option'
    );
    const visibleCandidates = [
      visibleValue?.textContent,
      visibleValue?.getAttribute('data-swatch-value'),
      selectedDropdownOption?.textContent,
      selectedDropdownOption?.dataset.value,
    ];

    for (const candidate of visibleCandidates) {
      if (normalizeText(candidate).includes('please select')) return null;
      const width = Number.parseFloat(String(candidate || '').replace(/,/g, ''));
      if (Number.isFinite(width) && width > 0) return width;
    }

    for (const select of fields) {
      const selectedOption = select.options?.[select.selectedIndex];
      const selectedText = String(selectedOption?.textContent || select.value || '').trim();
      if (!selectedText || normalizeText(selectedText).includes('please select')) continue;
      const width = Number.parseFloat(selectedText.replace(/,/g, ''));
      if (Number.isFinite(width) && width > 0) return width;
    }

    return null;
  }

  function getCurtainHeaderWidthDivisor(productOptionsRoot) {
    const headerWidthDivisors = {
      'ripple fold': 2,
      'grommet top': 2,
      'pinch pleat - double': 2,
      'pinch pleat - triple': 2.5,
      'euro pleat - double': 2,
      'euro pleat - triple': 2.5,
      'back tab': 2.5,
    };
    const checkedHeader = productOptionsRoot.querySelector([
      'input[key="po_Header"]:checked',
      'input[name="Header"]:checked',
      'input[name="properties[po_Header]"]:checked',
      'input[name="properties[Header]"]:checked',
    ].join(','));
    const checkedValue = normalizeText(checkedHeader?.value);
    const checkedHeaderName = Object.keys(headerWidthDivisors).find((name) => checkedValue.includes(name));
    if (checkedHeaderName) return headerWidthDivisors[checkedHeaderName];

    const headerStack = Array.from(productOptionsRoot.querySelectorAll('.po-stack')).find((stack) => (
      normalizeMatchKey(getOptionStackName(stack)) === 'header'
    ));
    const visibleValue = normalizeText(
      headerStack?.querySelector('.po-selected-swatch, .po-value, .po-dropdown-selected-option')?.textContent ||
      getOptionLabelElement(headerStack)?.textContent
    );
    const visibleHeaderName = Object.keys(headerWidthDivisors).find((name) => visibleValue.includes(name));
    return visibleHeaderName ? headerWidthDivisors[visibleHeaderName] : null;
  }

  function formatCurtainWidthTipNumber(value) {
    return Number(value).toFixed(2).replace(/\.?0+$/, '');
  }

  function updateCurtainWidthTip(productOptionsRoot) {
    const sizeLabel = Array.from(productOptionsRoot.querySelectorAll('.po-label, .po-label span')).find((item) => (
      isSinglePanelSizeOption(cleanOptionLabel(item.textContent))
    ));
    const sizeStack = sizeLabel?.closest('.po-stack');
    if (!sizeStack) return false;

    let tip = sizeStack.querySelector('[data-curtain-width-tip]');
    if (!tip) {
      tip = document.createElement('div');
      tip.className = 'ripple-fold-width-tip';
      tip.dataset.curtainWidthTip = '';

      const helpRow = sizeStack.querySelector(
        '[data-product-integrations-injected="size-helptext-link"], .option-help-size-helptext'
      );
      const insertTarget = helpRow?.closest('[data-product-integrations-injected="size-helptext-link"]') || helpRow;
      if (insertTarget) {
        insertTarget.insertAdjacentElement('afterend', tip);
      } else {
        const labelContainer = sizeStack.querySelector('.po-label-container');
        labelContainer?.insertAdjacentElement('afterend', tip);
      }
    }

    const fabricWidth = getSelectedCurtainWidth(sizeStack);
    const widthDivisor = getCurtainHeaderWidthDivisor(productOptionsRoot);
    const shouldHide = !fabricWidth || !widthDivisor;
    if (tip.hidden !== shouldHide) tip.hidden = shouldHide;
    if (tip.hidden) return true;

    const finishedWidth = Math.ceil(fabricWidth / widthDivisor);
    const contentKey = `${fabricWidth}|${widthDivisor}|${finishedWidth}`;
    if (tip.dataset.curtainWidthTipKey === contentKey) return true;
    tip.dataset.curtainWidthTipKey = contentKey;
    tip.innerHTML = `
      <div class="ripple-fold-width-tip__title">
        <span class="ripple-fold-width-tip__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="9" cy="9" r="7.5" stroke="currentColor" stroke-width="1.5"></circle>
            <path d="M9 8v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
            <circle cx="9" cy="5.25" r="0.75" fill="currentColor"></circle>
          </svg>
        </span>
        <span>Please Note*</span>
      </div>
      <p class="ripple-fold-width-tip__text">${formatCurtainWidthTipNumber(fabricWidth)}"  fabric width covers approximately <span class="ripple-fold-width-highlight">${formatCurtainWidthTipNumber(finishedWidth)}" </span>when pleated. Need help with sizing? <a href="#" class="ripple-fold-width-tip__chat-link" data-open-chat="true">Contact us</a>.</p>
    `;
    return true;
  }

  function updateAllCurtainWidthTips(root) {
    Array.from(root.querySelectorAll(PRODUCT_OPTIONS_SELECTOR)).forEach(updateCurtainWidthTip);
  }

  function initCurtainWidthTip(root, config, productOptionsRoot) {
    if (!config.measurementHelpEnabled || !isCurtainsProduct(config)) return;

    let updateFrame = null;
    const scheduleUpdate = () => {
      if (updateFrame !== null) return;
      updateFrame = window.requestAnimationFrame(() => {
        updateFrame = null;
        updateCurtainWidthTip(productOptionsRoot);
      });
    };
    const isCurtainWidthTrigger = (target) => {
      const stack = target.closest?.('.po-stack');
      const optionName = normalizeMatchKey(stack ? getOptionStackName(stack) : '');
      return optionName.includes('header') || optionName.includes('width') || optionName === 'size' || optionName === 'singlepanelsize';
    };

    scheduleUpdate();
    productOptionsRoot.addEventListener('change', (event) => {
      if (isCurtainWidthTrigger(event.target)) scheduleUpdate();
    });
    productOptionsRoot.addEventListener('click', (event) => {
      if (!event.target.closest('.po-option--swatch, .po-option__value, .po-option__swatch-wrapper, .bss_scroll')) return;
      if (isCurtainWidthTrigger(event.target)) scheduleUpdate();
    });
  }

  function selectFirstSwatch(root) {
    let ready = false;
    queryAll(root, '.po-option--swatch-container').forEach((container) => {
      if (container.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) {
        ready = true;
        return;
      }
      const firstInput = container.querySelector('.po-option--swatch:first-child input[type="radio"], .po-option--swatch:first-child input[type="checkbox"]');
      if (!firstInput) return;
      firstInput.checked = true;
      firstInput.closest('.po-option--swatch')?.classList.add('selected', 'active');
      firstInput.dispatchEvent(new Event('change', { bubbles: true }));
      ready = true;
    });
    return ready;
  }

  function initDefaultSwatches(root, productOptionsRoot) {
    waitForOptionDecoration(productOptionsRoot, () => selectFirstSwatch(root));
  }

  function valueFor(selector, scope) {
    return (scope || document).querySelector(selector)?.value || '';
  }

  function normalizeOptionValue(value) {
    const normalized = String(value || '').trim();
    if (!normalized) return '';

    const lower = normalized.toLowerCase();
    if (
      lower === '0' ||
      lower === '-- please select --' ||
      lower === 'please select' ||
      lower.indexOf('select ') === 0
    ) {
      return lower === '0' ? '0' : '';
    }

    return normalized;
  }

  function selectedOptionValue(selector, scope) {
    const select = (scope || document).querySelector(selector);
    if (!select) return '';
    return normalizeOptionValue(select.value || select.querySelector('option[selected="true"]')?.value);
  }

  function selectedOptionValueFrom(selectors, scope) {
    return selectors.map((selector) => selectedOptionValue(selector, scope)).find(Boolean) || '';
  }

  function syncStickyLabel(root) {
    const label = query(root, '.sticky-choose-label-wrapper');
    const color = valueFor('input[name="Color"]:checked', root);
    if (!label || !color) return;

    const width = selectedOptionValueFrom([
      'select[key="po_Width ( inches)"]',
      'select[key="po_Width(inches)"]',
      'select[name="Width (inches)"]',
      'select[name="Width(inches)"]',
    ], root);
    const widthFraction = selectedOptionValueFrom([
      'select[key="po_Width Fraction"]',
      'select[name="Width Fraction"]',
    ], root);
    const length = selectedOptionValueFrom([
      'select[key="po_Length(inches)"]',
      'select[key="po_Height(inches)"]',
      'select[key="po_Height (inches)"]',
      'select[name="Height (inches)"]',
      'select[name="Height(inches)"]',
    ], root);
    const lengthFraction = selectedOptionValueFrom([
      'select[key="po_Length Fraction"]',
      'select[key="po_Height Fraction"]',
      'select[name="Height Fraction"]',
    ], root);
    const nextLabel = [
      color,
      valueFor("input[key='po_Mount Type']:checked", root),
      width ? `${width} '' ${widthFraction || '0'}W` : '',
      length ? `* ${length} '' ${lengthFraction || '0'}H` : '',
      valueFor("input[key^='po_Lining Type']:checked", root),
      valueFor("input[key='po_Lift Styles']:checked", root),
    ].filter(Boolean).join(' / ').replace('/ *', '*');
    if (label.textContent !== nextLabel) label.textContent = nextLabel;
  }

  function initOptionEvents(root) {
    if (!once(root, 'OptionChangeBound')) return;
    root.addEventListener('change', () => {
      syncStickyLabel(root);
    });
  }

  function updateQuantityUnit(root) {
    queryAll(root, '.quantity-input').forEach((input) => {
      const quantity = Number(input.value) || 1;
      const unit = input.closest('.product-form__quantity')?.querySelector('[data-quantity-unit], .quantity-unit');
      if (unit) unit.textContent = quantity >= 2 ? 'Panels' : 'Panel';
    });
  }

  function initQuantityUnit(root) {
    let updateFrame = null;
    const scheduleUpdate = () => {
      // One rAF-coalesced update per burst is enough; the previous extra
      // 100ms settle timer simply ran the same scan a second time on every
      // quantity keystroke.
      if (updateFrame === null) {
        updateFrame = window.requestAnimationFrame(() => {
          updateFrame = null;
          updateQuantityUnit(root);
        });
      }
    };

    updateQuantityUnit(root);
    root.addEventListener('input', (event) => {
      if (event.target.closest('.quantity-input, quantity-input')) scheduleUpdate();
    });
    root.addEventListener('change', (event) => {
      if (event.target.closest('.quantity-input, quantity-input')) scheduleUpdate();
    });
    root.addEventListener('click', (event) => {
      if (event.target.closest('quantity-input')) {
        scheduleUpdate();
      }
    });
  }

  function parseMeasurementResult(button) {
    try {
      return JSON.parse(button?.dataset?.value || '{}');
    } catch (error) {
      console.error('Measurement result is invalid', error);
      return null;
    }
  }

  function triggerNativeChange(element) {
    if (!element) return;
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function escapeAttributeValue(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function findDropdownOption(scope, value) {
    if (!scope) return null;
    const stringValue = String(value);
    const option = scope.querySelector(`.po-dropdown-option[data-value="${escapeAttributeValue(stringValue)}"]`);
    if (option) return option;

    return Array.from(scope.querySelectorAll('.po-dropdown-option')).find((item) => (
      item.getAttribute('data-value') === stringValue || item.textContent.trim() === stringValue
    ));
  }

  const numericOptionCandidatesBySelect = new WeakMap();

  function availableNumericValue(scope, value) {
    if (!scope) return null;
    const targetNumber = parseInchesNumber(value);
    if (!Number.isFinite(targetNumber)) return null;

    const select = scope.matches?.('select') ? scope : scope.querySelector('select');
    const cacheKey = select || scope;
    let candidates = numericOptionCandidatesBySelect.get(cacheKey);
    if (!candidates) {
      const source = select?.options?.length ? Array.from(select.options) : Array.from(scope.querySelectorAll('.po-dropdown-option'));
      candidates = source
        .map((item) => item.value || item.getAttribute('data-value') || item.textContent.trim())
        .map((itemValue) => ({
          raw: itemValue,
          number: parseInchesNumber(itemValue),
        }))
        .filter((item) => Number.isFinite(item.number));
      numericOptionCandidatesBySelect.set(cacheKey, candidates);
    }

    if (!candidates.length) return null;

    const exact = candidates.find((item) => item.number === targetNumber);
    if (exact) return exact.raw;

    const nearest = candidates.reduce((best, item) => (
      Math.abs(item.number - targetNumber) < Math.abs(best.number - targetNumber) ? item : best
    ), candidates[0]);

    return nearest.raw;
  }

  function clickBssDropdownOption(target, value) {
    if (!target) return false;
    const scopes = [
      target.previousElementSibling,
      target.closest?.('.po-stack'),
      target.closest?.('.po-option--container'),
      target,
    ].filter(Boolean);
    const option = scopes.map((scope) => findDropdownOption(scope, value)).find(Boolean)
      || scopes.map((scope) => availableNumericValue(scope, value)).filter(Boolean).map((numericValue) => (
        scopes.map((scope) => findDropdownOption(scope, numericValue)).find(Boolean)
    )).find(Boolean);
    if (!option) return false;
    const dropdown = option.closest('.po-dropdown');
    dropdown?.querySelector('.po-dropdown-selected-option')?.click();
    option.click();
    window.setTimeout(() => {
      closeBssDropdown(dropdown || option);
    }, 80);
    return true;
  }

  function closeBssDropdown(target) {
    const dropdown = target?.classList?.contains('po-dropdown') ? target : target?.closest?.('.po-dropdown');
    if (!dropdown) return;

    const selected = dropdown.querySelector('.po-dropdown-selected-option');
    const list = dropdown.querySelector('.po-dropdown-list');
    dropdown.classList.remove('open', 'active', 'is-open');
    selected?.classList.remove('open', 'active', 'is-open');
    list?.classList.remove('open', 'active', 'is-open');
    selected?.setAttribute('aria-expanded', 'false');

    if (document.activeElement && dropdown.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }

  function applySelectValue(select, value) {
    if (!select) return false;
    const nativeUpdated = setSelectValue(select, value);
    const dropdownUpdated = clickBssDropdownOption(select, value);
    return nativeUpdated || dropdownUpdated;
  }

  function setSelectValue(select, value) {
    if (!select) return false;
    const stringValue = String(value);
    const numericValue = availableNumericValue(select, value);
    const option = Array.from(select.options || []).find((item) => (
      item.value === stringValue || item.textContent.trim() === stringValue ||
      (numericValue && (item.value === numericValue || item.textContent.trim() === numericValue))
    ));
    if (!option) return false;
    select.value = option.value;
    Array.from(select.options || []).forEach((item) => item.removeAttribute('selected'));
    option.setAttribute('selected', 'true');
    triggerNativeChange(select);
    return true;
  }

  function normalizeFieldLabel(value) {
    return normalizeText(value).replace(/\s*\*\s*/g, ' ').trim();
  }

  function fieldLabelMatches(actual, expected) {
    const normalizedActual = normalizeFieldLabel(actual);
    const normalizedExpected = normalizeFieldLabel(expected);
    return normalizedActual === normalizedExpected || normalizedActual.startsWith(`${normalizedExpected} `);
  }

  function stackByFieldLabel(labelText) {
    const stacks = document.querySelectorAll('.po-stack');

    for (const stack of stacks) {
      const label = stack.querySelector('.po-label span, .po-label');
      if (!fieldLabelMatches(label?.textContent, labelText)) continue;

      return stack;
    }

    return null;
  }

  function getFirstFieldTarget(selectors, labels, scopeSelectors = []) {
    for (const selector of selectors) {
      if (!selector) continue;
      if (selector && typeof selector !== 'string') return selector;
      const select = query(document, selector);
      if (select) return select;
    }

    for (const selector of scopeSelectors) {
      if (!selector) continue;
      if (selector && typeof selector !== 'string') return selector;
      const scope = query(document, selector);
      if (scope) return scope;
    }

    for (const label of labels) {
      const stack = stackByFieldLabel(label);
      if (stack) return stack.querySelector('select') || stack;
    }

    return null;
  }

  function selectByAttributeWords(requiredWords, rejectedWords = []) {
    return Array.from(document.querySelectorAll('select')).find((select) => {
      const haystack = normalizeText([
        select.getAttribute('key'),
        select.getAttribute('name'),
        select.id,
        select.closest('.po-stack')?.querySelector('.po-label')?.textContent,
      ].filter(Boolean).join(' '));
      return requiredWords.every((word) => haystack.includes(word))
        && rejectedWords.every((word) => !haystack.includes(word));
    }) || null;
  }

  function findRoomNameInput() {
    let input = document.querySelector('input[onchange*="\'Room Name\'"]');
    if (input) return input;

    const stacks = document.querySelectorAll('.po-stack');
    for (const stack of stacks) {
      const label = stack.querySelector('.po-label span, .po-label');
      if (!fieldLabelMatches(label?.textContent, 'Room Name')) continue;
      input = stack.querySelector('input.po-option--type-text, input[type="text"]');
      if (input) return input;
    }

    return null;
  }

  function fillRoomNameInput(roomName) {
    if (!roomName || roomName === 'baseRoom') return false;
    const input = findRoomNameInput();
    if (!input) return false;
    if (input.value === roomName) return true;
    input.value = roomName;
    triggerNativeChange(input);
    return true;
  }

  function fillRoomNameWithRetry(roomName) {
    if (fillRoomNameInput(roomName)) return;

    const optionRoot = document.querySelector('.bss-po-product-page');
    if (optionRoot) {
      let deadline = null;
      const observer = new MutationObserver(() => {
        if (!fillRoomNameInput(roomName)) return;
        observer.disconnect();
        window.clearTimeout(deadline);
      });
      observer.observe(optionRoot, { childList: true, subtree: true });
      deadline = window.setTimeout(() => observer.disconnect(), 10000);
      return;
    }

    // If the integration root itself is not available yet, retain the existing
    // bounded fallback rather than observing the entire document.
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (fillRoomNameInput(roomName) || tries >= 40) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  function parseInchesNumber(value) {
    if (typeof value === 'number') return value;
    const match = String(value || '').match(/-?\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : Number.NaN;
  }

  function splitInches(value) {
    const number = parseInchesNumber(value);
    if (!Number.isFinite(number)) return { whole: String(value), fraction: '' };

    const whole = Math.floor(number);
    const fraction = number - whole;
    const eighths = Math.round(fraction * 8);

    if (eighths <= 0) return { whole: String(whole), fraction: '0' };
    if (eighths >= 8) return { whole: String(whole + 1), fraction: '0' };

    const gcd = (a, b) => (b ? gcd(b, a % b) : a);
    const divisor = gcd(eighths, 8);
    return {
      whole: String(whole),
      fraction: `${eighths / divisor}/${8 / divisor}`,
    };
  }

  function normalizeMeasurementFraction(value) {
    const fraction = String(value || '').trim();
    if (!fraction || fraction === '0') return '0';
    return fraction;
  }

  function setSelectsValue(selects, value) {
    selects.filter(Boolean).forEach((select) => {
      applySelectValue(select, value);
    });
  }

  function setFieldTargetsValue(targets, value) {
    targets.filter(Boolean).forEach((target) => {
      if (target.matches?.('select')) {
        applySelectValue(target, value);
        return;
      }

      clickBssDropdownOption(target, value);
    });
  }

  function applyMeasurementResultToProduct(result) {
    if (!result || !result.width || !result.height) return;

    const width = result.width;
    const height = result.height;
    const header = result.name;
    const quantity = result.qty || 1;

    const getHeaderInput = () => Array.from(document.querySelectorAll('input[key="po_Header"], input[name="Header"]'))
      .find((input) => input.value === header);
    const getCurtainWidthSelects = () => [
      query(document, 'select[name="Single Panel Width Before Pleats Made (inch)"]'),
      query(document, 'select[key="po_Single Panel Width (inch)"]'),
      query(document, 'select[key="po_Single Panel – Finished Coverage Width (inch)"]'),
      query(document, 'select[name="Single Panel Width (inch)"]'),
      selectByAttributeWords(['single', 'panel', 'width', 'inch']),
      selectByAttributeWords(['finished', 'coverage', 'width', 'inch']),
    ].filter(Boolean);
    const getCurtainHeightSelects = () => [
      query(document, 'select[name="Finished Length/Height (inch)"]'),
      query(document, 'select[name="Finished Length/Height (inch):"]'),
      query(document, 'select[name="Finished Length/Height (Inch)"]'),
      query(document, 'select[name="Finished Length/Height (Inch):"]'),
      query(document, 'select[key="po_Single Panel Length (Height) in Inch"]'),
      selectByAttributeWords(['finished', 'length', 'height', 'inch']),
      selectByAttributeWords(['single', 'panel', 'length', 'height', 'inch']),
    ].filter(Boolean);
    const getWidthWholeTarget = () => getFirstFieldTarget([
      'select[key="po_Width ( inches)"]',
      'select[key="po_Width(inches)"]',
      'select[name="Width (inches)"]',
      'select[name="Width ( inches)"]',
      selectByAttributeWords(['width', 'inch'], ['fraction']),
    ], ['Width (inches)'], ['.self-width-input-wrapper .po-stack:first-child']);
    const getWidthFractionTarget = () => getFirstFieldTarget([
      'select[key="po_Width Fraction"]',
      'select[name="Width Fraction"]',
      selectByAttributeWords(['width', 'fraction']),
    ], ['Width Fraction'], ['.self-width-input-wrapper .po-stack:last-child']);
    const getHeightWholeTarget = () => getFirstFieldTarget([
      'select[key="po_Length(inches)"]',
      'select[key="po_Height(inches)"]',
      'select[key="po_Height (inches)"]',
      'select[name="Height (inches)"]',
      'select[name="Height ( inches)"]',
      selectByAttributeWords(['height', 'inch'], ['fraction']),
      selectByAttributeWords(['length', 'inch'], ['fraction']),
    ], ['Height (inches)'], ['.self-height-input-wrapper .po-stack:first-child']);
    const getHeightFractionTarget = () => getFirstFieldTarget([
      'select[key="po_Length Fraction"]',
      'select[key="po_Height Fraction"]',
      'select[name="Height Fraction"]',
      selectByAttributeWords(['height', 'fraction']),
      selectByAttributeWords(['length', 'fraction']),
    ], ['Height Fraction'], ['.self-height-input-wrapper .po-stack:last-child']);

    window.setTimeout(() => {
      const headerInput = getHeaderInput();
      if (headerInput && !headerInput.checked) headerInput.click();
      triggerNativeChange(headerInput);
    }, 10);

    window.setTimeout(() => {
      const widthParts = splitInches(width);
      const widthFraction = normalizeMeasurementFraction(result.width_F || result.widthFraction || widthParts.fraction);
      setSelectsValue(getCurtainWidthSelects(), width);
      setFieldTargetsValue([getWidthWholeTarget()], widthParts.whole);
      setFieldTargetsValue([getWidthFractionTarget()], widthFraction);
    }, 600);

    window.setTimeout(() => {
      const heightParts = splitInches(height);
      const heightFraction = normalizeMeasurementFraction(result.height_F || result.heightFraction || heightParts.fraction);
      setSelectsValue(getCurtainHeightSelects(), height);
      setFieldTargetsValue([getHeightWholeTarget()], heightParts.whole);
      setFieldTargetsValue([getHeightFractionTarget()], heightFraction);

      const quantityInput = query(document, 'input[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = quantity;
        triggerNativeChange(quantityInput);
      }
    }, 800);
  }

  function formatProductMoney(cents) {
    const moneyFormat = window.themeGlobalVariables?.settings?.money_format || window.cartStrings?.money_format || '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  function getProductJson(root) {
    const productJson = query(root, '[data-product-json], .productJson');
    if (!productJson) return null;

    if (root._productViewJsonElement === productJson && root._productViewJsonData) {
      return root._productViewJsonData;
    }

    try {
      const data = JSON.parse(productJson.textContent || '{}');
      root._productViewJsonElement = productJson;
      root._productViewJsonData = data;
      root._productViewVariantMap = new Map(
        (Array.isArray(data?.variants) ? data.variants : []).map((variant) => [Number(variant.id), variant])
      );
      return data;
    } catch (error) {
      console.warn('Product JSON is invalid', error);
      return null;
    }
  }

  function getCurrentVariant(root, config) {
    const productData = getProductJson(root);
    const variants = Array.isArray(productData?.variants) ? productData.variants : [];
    if (!variants.length) return null;

    const variantInput = query(root, `#${config.productFormId} input[name="id"], input[name="id"]`);
    const variantId = Number(variantInput?.value || 0);

    return root._productViewVariantMap?.get(variantId) || variants[0] || null;
  }

  function hasEquivalentVariantPricing(previousVariant, currentVariant) {
    if (!previousVariant || !currentVariant) return false;

    const previousMeasurement = previousVariant.unit_price_measurement || {};
    const currentMeasurement = currentVariant.unit_price_measurement || {};
    return Boolean(previousVariant.available) === Boolean(currentVariant.available) &&
      Number(previousVariant.price || 0) === Number(currentVariant.price || 0) &&
      Number(previousVariant.compare_at_price || 0) === Number(currentVariant.compare_at_price || 0) &&
      Number(previousVariant.unit_price || 0) === Number(currentVariant.unit_price || 0) &&
      String(previousMeasurement.reference_value || '') === String(currentMeasurement.reference_value || '') &&
      String(previousMeasurement.reference_unit || '') === String(currentMeasurement.reference_unit || '');
  }

  function initNativeVariantPriceGuard(root, config) {
    if (!once(root, 'NativeVariantPriceGuard')) return;

    const bindPriceGuard = () => {
      const pickers = queryAll(root, 'variant-radios-detail');
      if (!pickers.length) return false;

      let ready = true;
      pickers.forEach((picker) => {
        if (picker.dataset.productViewPriceGuardBound === 'true') return;
        if (typeof picker.updatePrice !== 'function') {
          ready = false;
          return;
        }

        picker.dataset.productViewPriceGuardBound = 'true';
        let previousVariant = null;
        const updatePrice = picker.updatePrice;

        picker.addEventListener('change', (event) => {
          if (!event.target.closest?.('.product-form__input_color input[type="radio"]')) return;
          previousVariant = picker.currentVariant || getCurrentVariant(root, config);
        }, true);

        picker.updatePrice = function (productTarget) {
          const shouldKeepPrice = hasEquivalentVariantPricing(previousVariant, this.currentVariant);
          previousVariant = null;
          if (shouldKeepPrice) {
            root._holdProductViewEquivalentVariantPrice?.();
            return;
          }
          return updatePrice.call(this, productTarget);
        };
      });
      return ready;
    };

    if (bindPriceGuard()) return;
    customElements.whenDefined('variant-radios-detail').then(bindPriceGuard);
  }

  function syncProductViewPrice(root, config) {
    const variant = getCurrentVariant(root, config);
    if (!variant) return;

    const price = Number(variant.price || 0);
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const priceHtml = formatProductMoney(price);
    const compareHtml = compareAtPrice > price ? formatProductMoney(compareAtPrice) : '';

    queryAll(root, '.product-detail__price .product-view-price-value').forEach((priceNode) => {
      if (priceNode.innerHTML !== priceHtml) priceNode.innerHTML = priceHtml;
      if (priceNode.getAttribute('bss-po-variant-id') !== String(variant.id)) {
        priceNode.setAttribute('bss-po-variant-id', String(variant.id));
      }
      priceNode.classList.toggle('price--special', compareAtPrice > price);
    });

    queryAll(root, '.product-detail__price .product-view-compare-price').forEach((compareWrapper) => {
      let compareNode = compareWrapper.querySelector('.compare-price');

      if (!compareNode) {
        compareNode = document.createElement('s');
        compareNode.className = 'price-item compare-price dark-grey';
        compareWrapper.appendChild(compareNode);
      }

      if (compareNode.innerHTML !== compareHtml) compareNode.innerHTML = compareHtml;
      if (compareNode.getAttribute('bss-po-variant-id') !== String(variant.id)) {
        compareNode.setAttribute('bss-po-variant-id', String(variant.id));
      }
      compareWrapper.hidden = !compareHtml;
    });

    queryAll(root, '.product-detail__price .unit-price').forEach((unitPrice) => {
      const number = unitPrice.querySelector('.number');
      const unit = unitPrice.querySelector('.unit');
      const measurement = variant.unit_price_measurement;

      if (variant.unit_price && measurement) {
        unitPrice.hidden = false;
        if (number) {
          const unitPriceHtml = formatProductMoney(variant.unit_price);
          if (number.innerHTML !== unitPriceHtml) number.innerHTML = unitPriceHtml;
        }
        if (unit) {
          const unitHtml = `${measurement.reference_value !== 1 ? measurement.reference_value : ''}${measurement.reference_unit || ''}`;
          if (unit.innerHTML !== unitHtml) unit.innerHTML = unitHtml;
        }
      } else {
        unitPrice.hidden = true;
      }
    });

    if (compareAtPrice > price) {
      const msrpHtml = formatProductMoney(compareAtPrice);
      const savePriceHtml = formatProductMoney(compareAtPrice - price);

      queryAll(root, '[data-product-view-msrp-price]').forEach((msrpNode) => {
        if (msrpNode.innerHTML !== msrpHtml) msrpNode.innerHTML = msrpHtml;
        msrpNode.setAttribute('data-variant-id', String(variant.id));
      });

      queryAll(root, '[data-product-view-save-price]').forEach((savePriceNode) => {
        if (savePriceNode.innerHTML !== savePriceHtml) savePriceNode.innerHTML = savePriceHtml;
        savePriceNode.setAttribute('data-variant-id', String(variant.id));
      });
    }
  }

  function initProductViewPriceSync(root, config) {
    if (!once(root, 'PriceSync')) return;

    const innerHtmlDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
    const guardedPriceNodes = new Set();
    const heldPriceHtml = new Map();
    let priceHoldActive = false;
    let priceHoldSettleTimer = null;
    let priceHoldSafetyTimer = null;

    const releasePriceHold = () => {
      priceHoldActive = false;
      heldPriceHtml.clear();
      clearTimeout(priceHoldSettleTimer);
      clearTimeout(priceHoldSafetyTimer);
      priceHoldSettleTimer = null;
      priceHoldSafetyTimer = null;
    };

    const beginEquivalentVariantPriceHold = () => {
      priceHoldActive = true;
      heldPriceHtml.clear();
      guardedPriceNodes.forEach((priceNode) => {
        heldPriceHtml.set(priceNode, innerHtmlDescriptor.get.call(priceNode));
      });
      clearTimeout(priceHoldSettleTimer);
      clearTimeout(priceHoldSafetyTimer);
      priceHoldSettleTimer = null;
      priceHoldSafetyTimer = setTimeout(releasePriceHold, 1800);
    };

    root._holdProductViewEquivalentVariantPrice = beginEquivalentVariantPriceHold;

    const bindPriceWriteGuards = () => {
      if (!innerHtmlDescriptor?.get || !innerHtmlDescriptor?.set) return;

      queryAll(root, '.product-detail__price .product-view-price-value, .product-detail__price .product-view-compare-price s')
        .forEach((priceNode) => {
          guardedPriceNodes.add(priceNode);
          if (priceNode.dataset.productViewPriceWriteGuardBound === 'true') return;
          priceNode.dataset.productViewPriceWriteGuardBound = 'true';

          Object.defineProperty(priceNode, 'innerHTML', {
            configurable: true,
            enumerable: innerHtmlDescriptor.enumerable,
            get() {
              return innerHtmlDescriptor.get.call(this);
            },
            set(value) {
              const nextHtml = String(value);
              const currentHtml = innerHtmlDescriptor.get.call(this);
              if (priceHoldActive) {
                const heldHtml = heldPriceHtml.get(this) ?? currentHtml;
                heldPriceHtml.set(this, heldHtml);
                if (nextHtml === heldHtml) {
                  clearTimeout(priceHoldSettleTimer);
                  priceHoldSettleTimer = setTimeout(releasePriceHold, 120);
                }
                return;
              }
              if (currentHtml === nextHtml) return;
              innerHtmlDescriptor.set.call(this, value);
            },
          });
        });
    };

    let syncFrame = null;
    const syncSoon = () => {
      if (syncFrame !== null) return;
      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = null;
        bindPriceWriteGuards();
        syncProductViewPrice(root, config);
      });
    };

    root.addEventListener('change', (event) => {
      if (priceHoldActive && event.target.closest(PRODUCT_OPTIONS_SELECTOR)) releasePriceHold();
      if (
        event.target.closest('variant-radios-detail') ||
        event.target.matches(`#${config.productFormId} input[name="id"], input[name="id"]`)
      ) {
        syncSoon();
      }
    });

    root.addEventListener('click', (event) => {
      if (priceHoldActive && event.target.closest(PRODUCT_OPTIONS_SELECTOR)) releasePriceHold();
    }, true);

    queryAll(root, '.product-detail__price').forEach((priceContainer) => {
      new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.addedNodes.length > 0)) bindPriceWriteGuards();
      }).observe(priceContainer, { childList: true, subtree: true });
    });

    bindPriceWriteGuards();
    syncProductViewPrice(root, config);
  }

  function closeMeasurementTool() {
    const parentSec = document.querySelector('.parent-sec');
    const maskParent = document.querySelector('.nyg-mask-box-parent');
    if (maskParent) maskParent.style.display = 'none';
    if (parentSec) parentSec.style.display = 'none';
  }

  function clearMeasurementSessionStorage() {
    [
      'baseRoom',
      'baseRoomRoman',
      'measurementProductPath',
      'roomName',
      'roomNameRoman',
      'roomname',
      'roomnameroman',
    ].forEach((key) => sessionStorage.removeItem(key));
  }

  function initMeasurementModal() {
    if (document.documentElement.dataset.productIntegrationsMeasurementBound === 'true') return;
    document.documentElement.dataset.productIntegrationsMeasurementBound = 'true';

    const currentProductPath = window.location.pathname;
    const storedProductPath = sessionStorage.getItem('measurementProductPath');
    if ((storedProductPath && storedProductPath !== currentProductPath) || (!storedProductPath && sessionStorage.getItem('baseRoom'))) {
      localStorage.removeItem('measurementProductResult');
      clearMeasurementSessionStorage();
    }

    document.addEventListener('click', (event) => {
      const shopButton = event.target.closest('.shop-btn');
      if (!shopButton) return;

      const result = parseMeasurementResult(shopButton);
      if (result) {
        const roomName = document.querySelector('.room-name-input')?.value || 'baseRoom';
        const resultWithRoom = {
          ...result,
          room_name: roomName,
        };

        localStorage.setItem('measurementProductResult', JSON.stringify(resultWithRoom));
        sessionStorage.setItem('measurementProductPath', currentProductPath);
        sessionStorage.setItem('baseRoom', JSON.stringify({
          ...resultWithRoom,
          r_width: result.width,
          r_height: result.height,
          r_qty: result.qty,
        }));
        applyMeasurementResultToProduct(resultWithRoom);
        fillRoomNameWithRetry(roomName);
      }

      closeMeasurementTool();
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('.product_submit_button')) {
        localStorage.removeItem('measurementProductResult');
        clearMeasurementSessionStorage();
        localStorage.removeItem('baseRoomRoman');
      }
    }, true);

    window.addEventListener('pagehide', () => {
      localStorage.removeItem('measurementProductResult');
      clearMeasurementSessionStorage();
    });
  }

  function initDeferredProductContent(root) {
    const productSection = root.closest('.main-product-section');
    if (!document.body.classList.contains('product') || productSection?.parentElement?.tagName !== 'MAIN') return;

    // Match the content roots in main-product.css, never OPTIS or every app.
    const selector = '.product-tabs, .sec__product-content, '
      + '#judgeme_product_reviews';
    const pending = new Set();
    for (let section = productSection.nextElementSibling; section; section = section.nextElementSibling) {
      if (!section.classList.contains('shopify-section')) continue;
      section.querySelectorAll(selector).forEach((element) => pending.add(element));
    }
    if (!pending.size) return;

    let observer;
    const listeners = new AbortController();
    const reveal = (element) => {
      element.setAttribute('data-product-content-ready', '');
      // Finish deferred description transforms before review/anchor handlers
      // measure their destination; otherwise first-use layout can move it.
      element.dispatchEvent(new Event('product-view:content-reveal'));
      observer?.unobserve(element);
      pending.delete(element);
      if (!pending.size) {
        observer?.disconnect();
        listeners.abort();
      }
    };
    const revealAll = () => pending.forEach(reveal);
    const revealTarget = (target) => {
      if (!(target instanceof Element)) return;
      pending.forEach((element) => {
        if (element.contains(target)) reveal(element);
      });
    };
    const revealThrough = (target) => {
      if (!(target instanceof Element)) return;
      pending.forEach((element) => {
        if (element.contains(target) || (element.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING)) {
          reveal(element);
        }
      });
    };
    if (window.Shopify?.designMode || window.location.hash || !('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    // Release before the existing 700–800px component/asset observers run.
    // Once revealed, leave containment off: descendants may open fixed dialogs.
    observer = new IntersectionObserver((entries) => {
      // A fast scroll must not resize skipped sections above the viewport.
      entries.filter((entry) => entry.isIntersecting).forEach((entry) => reveal(entry.target));
    }, { rootMargin: '1200px 0px' });
    pending.forEach((element) => observer.observe(element));

    const revealForNavigation = (event) => {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('.jdgm-preview-badge--with-link')) {
        revealThrough(document.getElementById('judgeme_product_reviews'));
        return;
      }
      const link = event.target.closest('a[href]');
      if (!link) return;
      try {
        const url = new URL(link.href, document.baseURI);
        if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !url.hash) return;
        revealThrough(document.getElementById(decodeURIComponent(url.hash.slice(1))));
      } catch (_) { /* Malformed fragments are left to the browser. */ }
    };
    // Window capture precedes third-party document handlers that measure offsets.
    const capture = { capture: true, signal: listeners.signal };
    window.addEventListener('click', revealForNavigation, capture);
    window.addEventListener('keydown', revealForNavigation, capture);
    window.addEventListener('focusin', (event) => revealTarget(event.target), capture);
    window.addEventListener('hashchange', () => {
      try {
        revealTarget(document.getElementById(decodeURIComponent(location.hash.slice(1))));
      } catch (_) { /* Malformed fragments are left to the browser. */ }
    }, { signal: listeners.signal });
    window.addEventListener('beforeprint', revealAll, { signal: listeners.signal });
  }

  function initSizeGuide(root) {
    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-product-size-guide-trigger]');
      if (!trigger || !root.contains(trigger) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const template = root.querySelector('template[data-product-size-guide-content]');
      if (!template || !window.themePopup) return;
      // A normal page link remains usable if the popup runtime is unavailable.
      trigger.focus({ preventScroll: true });
      const popup = window.themePopup.open({
        id: `product-size-guide-${root.id}`,
        title: trigger.dataset.sizeGuideTitle,
        content: template.content.cloneNode(true),
        size: 'large',
      });
      if (popup) event.preventDefault();
    });
  }

  async function init(config) {
    const root = getRoot(config);
    if (!root || !once(root, 'Init')) return;
    const purchaseState = initProductPurchaseState(root, config);

    deferredProductStylesUrl = config.deferredCssUrl || deferredProductStylesUrl;
    initDeferredProductContent(root);
    initProductViewRum(config);

    initSwatchDrawerDelegation();
    initReviews(root);
    initSizeGuide(root);
    initProductOptionActionLinks(root, config);
    initOptionHelpDelegation();
    initNativeVariantPriceGuard(root, config);
    initNativeVariantSelection(root);
    initProductViewPriceSync(root, config);

    if (!config.requiresProductOptions) return;

    try {
      const productOptionsRoot = await waitForProductOptions(root, 40000, config);
      initOptisPropertySubmission(root, config, productOptionsRoot);
      initQuantityUnit(root);
      initOptionEvents(root);
      purchaseState.ready();

      scheduleAfterNextPaint(() => {
        if (config.measurementHelpEnabled) {
          initSizeHelpTextLink(root, config, productOptionsRoot);
        }
        queryAll(root, PRODUCT_OPTIONS_SELECTOR).forEach((optionsRoot) => {
          initCurtainWidthTip(root, config, optionsRoot);
        });
        initOptionNumbers(root, productOptionsRoot);
        bindOptionHelpTriggers(root, config, productOptionsRoot);
        initDefaultSwatches(root, productOptionsRoot);
        initConditionalOptionGroups(root, productOptionsRoot);
        syncStickyLabel(root);
      });
    } catch (error) {
      purchaseState.fail(error.message);
      if (config.requiresProductOptions) console.warn(error.message);
    }
  }

  window.ProductIntegrations = {
    openMeasurementTool(event, action) {
      const configNode = document.querySelector(CONFIG_SELECTOR);
      const config = parseConfig(configNode || { textContent: '{}' });
      return openMeasurementToolOverlay(event || new Event('click'), config, action || getMeasurementToolAction(config));
    },
    init,
    initAll() {
      document.querySelectorAll(CONFIG_SELECTOR).forEach((node) => {
        if (node.dataset.productIntegrationsConfigLoaded === 'true') return;
        node.dataset.productIntegrationsConfigLoaded = 'true';
        init(parseConfig(node));
      });
    },
  };

  onReady(() => window.ProductIntegrations.initAll());

}());
